import { Injectable } from "@angular/core";
import { Action, createSelector, Store } from "@ngrx/store";
import { IAppState } from "../store/state/app.state";
import { combineLatest, empty, fromEvent, Observable, of, timer } from "rxjs";
import {
    DuplicateCommand,
    EnumConnectionType,
    EnumLoginViewMode,
    EnumStationFilterType,
    IAdnetState,
    IAppSettingsWrap,
    IAppStatic,
    IBusinessDomainCollection,
    ICart,
    ICartProduct,
    ICatalogItemCategories,
    ICatalogItemInfos,
    ICatalogItemResources,
    ICatalogItems,
    ICategoryValues,
    IChangeListIds,
    ICRUDScene,
    Id,
    IDynawebsServerReply,
    IEventBlock,
    IEventParam,
    IEventScope,
    IIot,
    ILocalStorageUpdates,
    ILockedRecord,
    ILog,
    ILoginForm,
    IMusicChannel,
    IMusicChannelSong,
    IOrderDetails,
    IOrderForm,
    IOS,
    IPaintSource,
    IPreloadResellerInfo,
    IResource,
    IResourceStatistic,
    IPlayerProps,
    ISceneTemplateImport,
    ISceneTreeSliceResults,
    ISchedule,
    IScheduleEvent,
    IScheduleSelection,
    ISelectedSceneSceneId,
    ISharedResourceLibrary,
    IStation,
    IStationData,
    IStationGeoIp,
    IStationStatsV1,
    ISVGConfig,
    ITransitionPool,
    ITransitionPoolItem, IUIConfigState,
    IUIDuplicationBlock,
    IUser,
    IUserState,
    PlatformStatic,
    IAnimationEngine, ISceneBlock, IGlobalSettings, IGlobalSettingKey, IPreferencesBusiness, ISelectedTouch, ISceneBreadcrumb,
} from "../interfaces";
import {
    DEFAULT_RESELLER_ID,
    formatBytes,
    getAllEventsInScenes,
    getPlayerIdInScene,
    isDigitNoEmpty,
    isHostDev, isHostLocal,
    isMimeTypeExists,
    isNoValue,
    isNullOrUndefOrEmpty,
    isSceneDesktopEmpty,
    isSceneEmbedded,
    isMainTouchScene,
    NONE_VALUE,
    sceneFindEventInScenes,
    SUPPORTED_FILE_TYPES,
} from "../common";
import {
    IBlockLabelProp,
    IBlockLabelsRecords,
    MimeAnomalyType,
} from "../containers/editor/scene-blocks/block-constants";
import { actionAccountSendEmail, actionLogger } from "../store/actions/user.actions";
import { DRILL_PATH_KEY, IDrillPath, UNDOABLE_ACTION_TYPES } from "../store/reducers/undoable-actions";
import { ContainerService } from "./container.service";
import {
    IEnterpriseReseller,
    IGroupsPrivileges,
    IInstalledApp,
    IPrivileges,
    IStoreApps,
    IWhiteLabel,
} from "../interfaces/enterprise-interface";
import { IAccountDetails } from "../interfaces/enterprise-account-interface";
import { IBusinessAccount, IEnterpriseBusinesses } from "../interfaces/enterprise-businesses-interface";
import { IEnterpriseBusinessUsers } from "../interfaces/enterprise-business-users-interface";
import { List } from "immutable";
import { BusinessModel } from "../containers/adnet/models/BusinessModel";
import { AdnetCustomerModel } from "../containers/adnet/models/AdnetCustomerModel";
import { IAdnetCustomerCredentials } from "../interfaces/adnet-interfaces";
import { AdnetRateModel } from "../containers/adnet/models/AdnetRateModel";
import { AdnetTargetModel } from "../containers/adnet/models/AdnetTargetModel";
import { HttpClient } from "@angular/common/http";
import { AdnetPairModel } from "../containers/adnet/models/AdnetPairModel";
import { AdnetPackageModel } from "../containers/adnet/models/AdnetPackageModel";
import { AdnetReportModel } from "../containers/adnet/models/AdnetReportModel";
import { AdnetTransferModel } from "../containers/adnet/models/AdnetTransferModel";
import { AdnetPaymentModel } from "../containers/adnet/models/AdnetPaymentModel";
import { ISocialServerReply } from "../interfaces/social-interface";
import { SocialUser } from "@abacritt/angularx-social-login";
import { SceneMimeTypes } from "../containers/editor/scene-blocks/mime-constants";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { Lib } from "../../libs/Lib";
import { FasterqLineModel } from "../models/fasterq-line-model";
import { FasterqQueueModel } from "../models/fasterq-queue-model";
import { FasterqAnalyticsModel } from "../models/fasterq-analytics";
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, take, takeWhile } from "rxjs/operators";
import { ILocalStoreTypes, LocalStoreService } from "./local-store.service";
import { createHistorySelectors } from "ngrx-wieder";
import { IChatGlobal } from "../interfaces/chat-interface";
import { isTruthy } from "../common/format";
import { isArray } from "lodash";
import moment from "moment";
import * as _ from "lodash";
import { isNullOrUndefined } from "util";

declare var platform: PlatformStatic;

const { version: version } = require("../../../package.json");

const selectFeature = (state: IAppState) => state.users;
const {
    selectCanUndo,
    selectCanRedo,
} = createHistorySelectors<IAppState, IUserState>(selectFeature);

/**
 * StoreSelectorsService - The Single Facade/Gateway to NgRx Store
 *
 * @description
 * This service is THE MOST CRITICAL service in SignStudio for accessing NgRx store data.
 * It serves as the single, centralized facade/gateway for all store operations, providing:
 *
 * - Observable selectors for all store slices (users, scenes, resources, stations, etc.)
 * - Dispatch methods for triggering store actions
 * - Utility methods for block/component management and validation
 * - Type-safe access to store data with proper filtering and transformation
 * - Performance-optimized selectors using createSelector and memoization
 *
 * **IMPORTANT**: All components should use this service instead of directly accessing the store.
 * This ensures consistent data access patterns, proper filtering, and centralized store logic.
 *
 * @remarks
 * Key Features:
 * - **Block Management**: Comprehensive methods for managing scene blocks/components (labels, types, validation)
 * - **User & Authentication**: User data, privileges, access masks, group permissions
 * - **Scene Management**: Scene data, scene items, player props, embedded scenes, private children
 * - **Resource Management**: Resources, shared libraries, storage statistics
 * - **Station Management**: Station data, stats, geo IPs, connection status
 * - **Enterprise Features**: Business accounts, reseller info, white label, apps, privileges
 * - **Adnet Features**: Customers, rates, targets, packages, reports, payments
 * - **Shopping Cart**: Cart products, order management, billing
 * - **Schedule Management**: Schedules, schedule events, calendar integration
 * - **FasterQ**: Queue lines, queue models, analytics, terminal
 * - **Event Management**: Event blocks, event params, event strings
 * - **Undo/Redo**: Undo/redo state management with ngrx-wieder
 *
 * @example
 * ```typescript
 * // Component usage - accessing user data
 * constructor(private storeSelector: StoreSelectorsService) {}
 *
 * ngOnInit() {
 *     // Get current user Observable
 *     this.storeSelector.getUser()
 *         .subscribe(user => console.log('Current user:', user));
 *
 *     // Get scenes Observable
 *     this.storeSelector.getScenes()
 *         .subscribe(scenes => console.log('All scenes:', scenes));
 *
 *     // Dispatch an action
 *     this.storeSelector.dispatch(actionUpdateScene({ scene }));
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Block label validation
 * const blockLabel = this.storeSelector.getBlockLabelById('3510');
 * const isJsonBased = this.storeSelector.isBlockIdJsonBased(blockId);
 * const imageTypes = this.storeSelector.getBlockLabelImageTypes();
 * ```
 */
@Injectable()
export class StoreSelectorsService {
    /** Internal storage for block label records/definitions */
    private m_blockLabelRecords: IBlockLabelsRecords;

    /** Counter for tracking newly added blocks in the scene editor */
    private m_addedBlockCount = 0;

    /** Cached reference to current user for quick synchronous access */
    private m_user: IUser;

    /** Memoized selector for user state (performance optimization) */
    private selectorGetUsers$;

    /** Memoized combined selector for user + config state (performance optimization) */
    private selectorCombined$;

    /**
     * Creates an instance of StoreSelectorsService
     *
     * @param store - NgRx store instance for accessing application state
     * @param _http - HTTP client for making server requests (used in Adnet operations)
     * @param toastr - Toast notification service for user feedback
     * @param translocoService - Translation service for internationalized messages
     */
    constructor(private store: Store<IAppState>, private _http: HttpClient, private toastr: ToastrService, private translocoService: TranslocoService, private container: ContainerService) {
        // Subscribe to user changes and cache locally for synchronous access
        this.getUser()
            .subscribe((i_user: IUser) => {
                this.m_user = i_user;
            });
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // UTILITY METHODS - Email & Logging
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Sends an error/feedback email to support with platform diagnostics
     *
     * @description
     * Dispatches an email action to send error reports or user feedback to support.
     * When error=true, automatically appends browser/platform diagnostics including
     * browser version, OS, user agent, and app version for debugging purposes.
     *
     * @param email - Recipient email address
     * @param description - Email body/description (will be augmented with platform info if error=true)
     * @param name - Name of the sender
     * @param error - If true, appends platform diagnostics to description
     *
     * @remarks
     * - Firefox browsers are excluded from error reporting due to known compatibility issues
     * - Platform diagnostics include: browser name, OS, user agent, browser version, app version
     * - Dispatches actionAccountSendEmail to trigger server-side email sending
     *
     * @example
     * ```typescript
     * // Send error report with diagnostics
     * this.storeSelector.sendEmail(
     *     'support@signage.com',
     *     'Scene editor crashed when duplicating blocks',
     *     'John Doe',
     *     true // Include platform diagnostics
     * );
     * ```
     */
    sendEmail(email, description, name, error) {
        if (error) {
            // require('platform');
            const info: any = platform.parse(platform.ua);
            const regex = RegExp("Edg");

            // firefox sucks
            const isFirefox = platform?.name?.toLowerCase()?.indexOf("firefox") > -1 || info?.name?.toLowerCase()?.indexOf("firefox") > -1;
            if (isFirefox) return;

            const platformInfo = `
                info ${info}<br/>
                name1: ${platform.name}<br/>
                name2: ${info.name}<br/>
                family: ${platform.os.family}<br/>
                description1: ${platform.description}<br/>
                description2: ${info.description}<br/>
                uaSting: ${platform.ua}<br/>
                isEdge: ${regex.test(platform.ua)}<br/>
                layout: ${info.layout}<br/>
                layout: ${info.layout}<br/>
                os: ${info.os}<br/>
                browserVersion: ${info.version}<br/>
                appVersion: ${version}<br/>
            `;
            description = `dump: ${description} <br/> ${platformInfo}`;
        }

        this.dispatch(actionAccountSendEmail({
            emailSend: { email, description, name },
        }));
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // BLOCK MANAGEMENT - Labels, Types & Validation
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Sets the block label records for scene component management
     *
     * @description
     * Stores the complete block label registry containing all component definitions,
     * types, templates, and metadata for scene blocks. This is typically set once
     * during app initialization from block-constants.ts
     *
     * @param i_blockLabelRecords - Complete block label records/definitions
     */
    set blockLabels(i_blockLabelRecords: IBlockLabelsRecords) {
        this.m_blockLabelRecords = i_blockLabelRecords;
    }

    /**
     * Gets the complete block label records
     *
     * @returns All block label definitions currently stored
     */
    get blockLabels(): IBlockLabelsRecords {
        return this.m_blockLabelRecords;
    }

    /**
     * Gets all supported image file types (includes IMAGE and SVG blocks)
     *
     * @returns Array of image mime types (e.g., ['image/png', 'image/jpeg', 'image/svg+xml', ...])
     *
     * @remarks
     * Combines types from both IMAGE and SVG block definitions
     */
    getBlockLabelImageTypes(): Array<string> {
        return [...this.getBlockLabelById(this.m_blockLabelRecords.IMAGE.id).types, ...this.getBlockLabelById(this.m_blockLabelRecords.SVG).types];
    }

    /**
     * Gets all supported video file types
     *
     * @returns Array of video mime types (e.g., ['video/mp4', 'video/webm', ...])
     */
    getBlockLabelVideoTypes(): Array<string> {
        return this.getBlockLabelById(this.m_blockLabelRecords.VIDEO.id).types;
    }

    /**
     * Finds a block label definition by file type/extension
     *
     * @param type - File type/extension to search for (e.g., 'mp4', 'jpg', 'png')
     * @returns Block label definition that supports this file type (deep cloned)
     *
     * @remarks
     * - Searches all block labels for matching file type in their types array
     * - Returns a deep clone to prevent accidental mutations
     * - Returns undefined if no matching block found
     */
    getBlockLabelByFileType(type): IBlockLabelProp {
        let res;
        // blockLabels is set once in a module constructor (MainModule / MigrateModule); guard against a
        // caller that runs before that so a missing registry returns undefined instead of throwing
        // "Cannot convert undefined or null to object" (which broke every SWF/FLV conversion on /migrate).
        Object.keys(this.blockLabels || {}).forEach((i_block) => {
            const block = this.blockLabels[i_block];
            if (!block.types) return;
            const idx = block.types.findIndex((z) => z === type);
            if (idx > -1) res = block;
        });
        res = _.cloneDeep(res);
        return res;
    }

    /**
     * Finds a block label definition by mime type with JSON filtering
     *
     * @param mimeType - Mime type to search for (e.g., 'Json.weather', 'video/mp4')
     * @param isJsonItemBased - If true, only return jsonItemBased blocks; if false, exclude them
     * @returns Block label definition matching the mime type and JSON criteria (deep cloned)
     *
     * @remarks
     * - Filters blocks based on jsonItemBased property (for JSON data sources)
     * - Used when adding components to scenes with specific mime types
     * - Returns deep clone to prevent mutations
     *
     * @example
     * ```typescript
     * // Get JSON-based block for weather data
     * const weatherBlock = this.storeSelector.getBlockLabelByMimeType('Json.weather', true);
     *
     * // Get non-JSON block for video
     * const videoBlock = this.storeSelector.getBlockLabelByMimeType('video/mp4', false);
     * ```
     */
    getBlockLabelByMimeType(mimeType: string, isJsonItemBased: boolean): IBlockLabelProp {
        const res = Object.values(this.blockLabels).find((block: IBlockLabelProp) => {
            if (isJsonItemBased) {
                if (block.id === "6500") {
                }
                if (!block.jsonItemBased) return false;
                if (isMimeTypeExists(block.types, mimeType)) return block;
            } else {
                if (block.jsonItemBased === true) return false;
                if (isMimeTypeExists(block.types, mimeType)) return block;
            }
        });
        return _.cloneDeep(res);
    }

    /**
     * Sets the counter for newly added blocks in scene editor
     *
     * @param i_addedBlockCount - Number of blocks added in current session
     *
     * @remarks
     * Used for tracking block additions for UI updates and analytics
     */
    set addedBlockCount(i_addedBlockCount: number) {
        this.m_addedBlockCount = i_addedBlockCount;
    }

    /**
     * Gets the counter for newly added blocks
     *
     * @returns Current count of added blocks
     */
    get addedBlockCount(): number {
        return this.m_addedBlockCount;
    }

    /**
     * Gets a block label definition by its unique ID
     *
     * @param id - Block ID (e.g., '3510' for IMAGE, '3240' for VIDEO)
     * @param clone - If true, returns deep clone; if false, returns reference (default: true)
     * @returns Block label definition
     *
     * @remarks
     * - Defaults to DesktopPro block (ID "0") if block not found
     * - Use clone=false carefully as mutations will affect the original object
     *
     * @example
     * ```typescript
     * // Get IMAGE block definition (safe clone)
     * const imageBlock = this.storeSelector.getBlockLabelById('3510');
     *
     * // Get reference (no clone) for read-only access
     * const videoBlock = this.storeSelector.getBlockLabelById('3240', false);
     * ```
     */
    getBlockLabelById(id, clone = true): IBlockLabelProp {
        let res = Object.values(this.blockLabels).find((k: IBlockLabelProp) => {
            if (k.id === id) {
                return k;
            }
        });
        /** default to DesktopPro if not found **/
        if (_.isUndefined(res)) res = this.getBlockLabelById("0");
        return clone ? _.cloneDeep(res) : res;
    }

    /**
     * Checks if a block is a label-based component (FasterQ labels)
     *
     * @param i_id - Block ID to check
     * @returns True if block is LABEL (3241) or LABELQUEUE (3242)
     *
     * @remarks
     * Label blocks are special FasterQ components for queue management
     */
    isBlockTypeLabelBased(i_id: Id): boolean {
        return i_id === '3241' || i_id === '3242'; // LABEL or LABELQUEUE block IDs
    }

    /**
     * Checks if a block uses JSON aliasing (alternative IDs stored in JSON data)
     *
     * @param i_sceneItem - Scene block/item to check
     * @returns The actual player ID (either from token alias or direct player ID)
     *
     * @remarks
     * - Some blocks store their true type in JSON data's token field
     * - Currently returns player ID as default (aliasing functionality reserved for future use)
     * - Used for determining the actual block type when processing scene items
     */
    isBlockIdJsonAliased(i_sceneItem: ISceneBlock): Id {
        const token = i_sceneItem.Data[0]?.Json?.[0]?.Data?.[0]?.$?.token;
        switch (token) {
            // case this.m_blockLabelRecords.NETFEED_HOROSCOPE.id:
            // case this.m_blockLabelRecords.NETFEED_NEWS.id: {
            //     return i_sceneItem.Data[0].Json[0].Data[0].$.token;
            // }
            default: {
                return i_sceneItem.$.player;
            }
        }
    }

    /**
     * Checks if a block is JsonItem-based (displays items from JSON data sources)
     *
     * @param i_id - Block ID to check
     * @returns True if block is a JsonItem component
     *
     * @remarks
     * JsonItem blocks display individual items from JSON feeds (weather, calendar, social media, etc.)
     */
    isBlockIdJsonItemBased(i_id: Id): boolean {
        const a = Object.values(this.m_blockLabelRecords).find((block: IBlockLabelProp) => {
            if (block.id === i_id) {
                if (block.name === "JsonItem") {
                    return true;
                }
            }
        });
        return !!a;
    }

    /**
     * Checks if mime type is a main scene container (Scene, Player, or Slides)
     *
     * @param mimeType - Mime type to check (e.g., 'Scene', 'Json.player', 'Json.slides')
     * @returns True if mime type is Scene, Player, or Slides
     *
     * @remarks
     * Main scene types are containers that hold other blocks/components
     */
    isMainSceneOrPlayer(mimeType: string): boolean {
        return mimeType === SceneMimeTypes.Scene || mimeType === SceneMimeTypes.Player || mimeType === SceneMimeTypes.Slides;
    }

    /**
     * Converts mime type to human-readable name
     *
     * @param mimeType - Mime type string (e.g., 'Json.weather', 'video.mp4')
     * @returns Formatted name (e.g., 'weather', 'video mp4')
     *
     * @remarks
     * - Removes 'Json.' prefix
     * - Replaces dots with spaces
     */
    mineToName(mimeType: string) {
        return mimeType.replace(/Json./, "").replace(/\./ig, " ");
    }

    /**
     * Checks if mime type is Player or Slides (excludes Scene)
     *
     * @param mimeType - Mime type to check
     * @returns True if mime type is Player or Slides only
     *
     * @remarks
     * Used when Scene type needs to be excluded but Player/Slides allowed
     */
    isMainScenePlayerOnly(mimeType: string): boolean {
        return mimeType === SceneMimeTypes.Player || mimeType === SceneMimeTypes.Slides;
    }

    /**
     * Checks if a block is JSON-based (loads data from JSON sources)
     *
     * @param i_id - Block ID to check
     * @returns True if block is jsonPlayerBased
     *
     * @remarks
     * JSON-based blocks load content from JSON feeds (RSS, social media, weather, etc.)
     */
    isBlockIdJsonBased(i_id: Id): boolean {
        const a = Object.values(this.m_blockLabelRecords).find((block: IBlockLabelProp) => {
            if (block.id === i_id) {
                if (block.types.length > 0 && block.jsonPlayerBased) {
                    return true;
                }
            }
        });
        return !!a;
    }

    /**
     * Checks if a block is item-based (displays individual items from collections)
     *
     * @param i_id - Block ID to check
     * @returns True if block is jsonItemBased
     *
     * @remarks
     * Item-based blocks display single items from data collections (catalog items, feed items, etc.)
     */
    isBlockItemBased(i_id: Id): boolean {
        const a = Object.values(this.m_blockLabelRecords).find((block: IBlockLabelProp) => {
            if (block.id === i_id) {
                if (block.jsonItemBased) {
                    return true;
                }
            }
        });
        return !!a;
    }

    /**
     * Checks if a block uses resource files (video, image, SVG)
     *
     * @param i_id - Block ID to check
     * @returns True if block is VIDEO, IMAGE, or SVG
     *
     * @remarks
     * Resource-based blocks require media files uploaded to the resource library
     */
    public isResourceBased(i_id: Id): boolean {
        const resourceIds = [
            this.m_blockLabelRecords.VIDEO.id,
            this.m_blockLabelRecords.IMAGE.id,
            this.m_blockLabelRecords.SVG.id,
        ];
        return resourceIds.indexOf(i_id) > -1;
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // USER & AUTHENTICATION SELECTORS
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Gets user access mask as array of enabled feature indices
     *
     * @returns Observable<Array<string>> - Array of enabled feature indices (e.g., ['0', '3', '5'])
     *
     * @description
     * Converts the user's binary access mask into an array of feature indices that are enabled.
     * The access mask controls which features/modules the user can access (e.g., Adnet, FasterQ, etc.)
     *
     * @remarks
     * - Uses distinctUntilChanged for performance (only emits when accessMask changes)
     * - Access mask is a binary string converted to array of enabled indices
     * - Each index represents a specific feature/module in the system
     *
     * @example
     * ```typescript
     * this.storeSelector.getUserAccessMask()
     *     .subscribe(enabledFeatures => {
     *         // enabledFeatures = ['0', '2', '4'] means features at indices 0, 2, 4 are enabled
     *         console.log('Enabled features:', enabledFeatures);
     *     });
     * ```
     */
    public getUserAccessMask(): Observable<Array<string>> {
        return this.getUser()
            .pipe(
                distinctUntilChanged((a, b) => {
                    return a.accessMask == b.accessMask;
                }),
                map((i_user: IUser) => {
                    const mask: Array<boolean> = Lib.GetAccessMask(i_user.accessMask);
                    return mask.map((v, i) => v ? String(i) : null).filter((z) => z !== null);
                }),
            );
    }

    /**
     * Gets user's group privileges (permissions based on group membership)
     *
     * @returns Observable<IGroupsPrivileges> - Group-based permissions
     *
     * @description
     * Returns privileges assigned to the user based on their group membership.
     * Used for enterprise/multi-user accounts to control feature access.
     *
     * @remarks
     * - Filters out null values (waits for privileges to load)
     * - Enterprise feature for managing user permissions via groups
     */
    public getGroupsPrivileges(): Observable<IGroupsPrivileges> {
        return this.store.select(store => store.users.selectedUser?.groupsPrivileges).pipe(filter((v) => v !== null));
    }

    /**
     * Gets a JSON-based block label by ID
     *
     * @param i_id - Block ID to look up
     * @returns Block label definition if it's JSON-based, undefined otherwise
     *
     * @remarks
     * Specifically searches for blocks with jsonPlayerBased=true and non-empty types array
     */
    getBlockJsonBasedById(i_id: Id): IBlockLabelProp {
        const a = Object.values(this.m_blockLabelRecords).find((block: IBlockLabelProp) => {
            if (block.id === i_id) {
                if (block.types.length > 0 && block?.jsonPlayerBased) {
                    return true;
                }
            }
        });
        return a;
    }

    /**
     * Deletes a specific property path from a component template
     *
     * @param component - Component object to modify
     * @param path - Lodash path to property to delete (e.g., 'template.Data[0].Json[0].$.displayMode')
     * @returns Cloned component with property removed
     *
     * @remarks
     * Uses lodash unset to safely remove nested properties
     */
    deleteInTemplates(component, path) {
        const c1 = _.cloneDeep(component);
        _.unset(c1, path);
        return c1;
    }

    /**
     * Swaps/merges templates between source and destination components
     *
     * @param source - Source component to copy from
     * @param destination - Destination component template
     * @param mimeType - Scene mime type context
     * @returns Component with swapped template
     *
     * @description
     * If mime type is main scene/player, returns destination as-is.
     * Otherwise, copies destination's template into source component.
     * Used when adapting components for different scene contexts.
     *
     * @remarks
     * Critical for JsonItem-based components that need template adaptation
     */
    swapTemplates(source, destination, mimeType) {
        if (this.isMainSceneOrPlayer(mimeType)) {
            return destination;
        } else {
            const c1 = _.cloneDeep(source);
            const c2 = _.cloneDeep(destination);
            c1.template = c2.template;
            return c1;
        }
    }

    /**
     * Gets the appropriate component definition for adding a block to a scene
     *
     * @param i_blockLabelIdOrMimeType - Either a block ID or mime type string
     * @param mimeType - Scene's mime type context (Scene, Player, Slides, or JSON types)
     * @returns Component definition ready for adding to scene
     *
     * @description
     * This is THE CORE METHOD for creating components when adding blocks to scenes.
     * It intelligently selects and adapts component templates based on:
     * - Block type being added
     * - Scene's mime type context
     * - Whether block is JSON-based, item-based, or resource-based
     *
     * The method handles 70+ component types including:
     * - Media blocks (Image, Video, SVG)
     * - JSON feeds (RSS, Weather, Calendar, Stocks, XML)
     * - Social media (Facebook, Instagram, Twitter, Pinterest, etc.)
     * - Interactive components (Buttons, Forms, Inputs, Sliders)
     * - Catalog/Database components
     * - Touch interface components
     * - FasterQ queue components
     * - Special blocks (HTML, Browser, QR, Chat, etc.)
     *
     * @remarks
     * - Automatically converts between JsonPlayer and JsonItem based on scene context
     * - Applies template swapping for compatibility
     * - Shows error toast if component type not supported
     * - Defaults to GENERIC component if block not found
     *
     * @example
     * ```typescript
     * // Adding weather component to a main scene
     * const weatherComponent = this.storeSelector.getComponentForBlock(
     *     'Json.weather',
     *     SceneMimeTypes.Player
     * );
     *
     * // Adding catalog item to a catalog scene
     * const catalogItemComponent = this.storeSelector.getComponentForBlock(
     *     this.blockLabels.CATALOG_ITEM.id,
     *     SceneMimeTypes.Catalog
     * );
     * ```
     */
    getComponentForBlock(i_blockLabelIdOrMimeType, mimeType: string): IBlockLabelProp {
        if (_.isNaN(Number(i_blockLabelIdOrMimeType))) {
            i_blockLabelIdOrMimeType = this.getBlockLabelByMimeType(i_blockLabelIdOrMimeType, false).id;
        }
        const blockLabelRecords = Object.assign({}, this.m_blockLabelRecords);
        const blockLabelId = String(i_blockLabelIdOrMimeType);
        let component: IBlockLabelProp;
        switch (blockLabelId) {
            case blockLabelRecords.BLOCKCODE_SCENE.id: {
                component = blockLabelRecords.BLOCKCODE_SCENE;
                break;
            }
            case blockLabelRecords.GROUP.id: {
                component = blockLabelRecords.GROUP;
                break;
            }
            case blockLabelRecords.JSON_PLAYER.id: {
                component = blockLabelRecords.JSON_PLAYER;
                break;
            }
            // case blockLabelRecords.NETFEED_NEWS.id: {
            //     component = blockLabelRecords.NETFEED_NEWS;
            //     break;
            // }
            // case blockLabelRecords.NETFEED_HOROSCOPE.id: {
            //     component = blockLabelRecords.NETFEED_HOROSCOPE;
            //     break;
            // }
            case blockLabelRecords.JSON_VIEWER.id: {
                component = blockLabelRecords.JSON_VIEWER;
                component = this.deleteInTemplates(component, "template.Data[0].Json[0].$.displayMode");
                break;
            }
            case blockLabelRecords.JSONITEM.id: {
                component = blockLabelRecords.JSONITEM;
                break;
            }
            case blockLabelRecords.SPREADSHEET.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.SPREADSHEET, mimeType);
                break;
            }
            case blockLabelRecords.DRIVE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.DRIVE, mimeType);
                break;
            }
            case blockLabelRecords.CUSTOMRSS_ITEM.id: {
                component = blockLabelRecords.CUSTOMRSS_ITEM;
                // component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.CUSTOMRSS_ITEM, mimeType);
                if (mimeType === SceneMimeTypes.CustomRss) {
                    component.template.$.player = blockLabelRecords.CUSTOMRSS_ITEM.id;
                }
                break;
            }
            case blockLabelRecords.CUSTOMRSS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.CUSTOMRSS, mimeType);
                if (this.isMainSceneOrPlayer(mimeType)) {
                    component.template.$.player = blockLabelRecords.CUSTOMRSS.id;
                }
                break;
            }
            case blockLabelRecords.CATALOG_ITEM.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.CATALOG_ITEM, mimeType);
                component.template.$.player = blockLabelRecords.CATALOG_ITEM.id;
                break;
            }
            case blockLabelRecords.CATALOG.id: {
                if (this.isMainSceneOrPlayer(mimeType)) {
                    component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.CATALOG, SceneMimeTypes.Player);
                    component.template.$.player = blockLabelRecords.CATALOG.id;
                } else {
                    component = blockLabelRecords.CATALOG_ITEM;
                    component.template.$.player = blockLabelRecords.CATALOG_ITEM.id;
                }
                break;
            }
            case blockLabelRecords.XML_ITEM.id: {
                component = blockLabelRecords.XML_ITEM;
                // component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.XML_ITEM, mimeType);
                if (mimeType === SceneMimeTypes.XML) {
                    component.template.$.player = blockLabelRecords.XML_ITEM.id;
                }
                break;
            }
            case blockLabelRecords.XML.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.XML, mimeType);
                if (this.isMainSceneOrPlayer(mimeType)) {
                    component.template.$.player = blockLabelRecords.XML.id;
                }
                break;
            }
            case blockLabelRecords.STOCKS_ITEM.id: {
                component = blockLabelRecords.STOCKS_ITEM;
                // component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.STOCKS_ITEM, mimeType);
                if (mimeType === SceneMimeTypes.Stocks) {
                    component.template.$.player = blockLabelRecords.STOCKS_ITEM.id;
                }
                break;
            }
            case blockLabelRecords.STOCKS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.STOCKS, mimeType);
                if (this.isMainSceneOrPlayer(mimeType)) {
                    component.template.$.player = blockLabelRecords.STOCKS.id;
                }
                break;
            }
            case blockLabelRecords.DROPBOX.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.DROPBOX, mimeType);
                break;
            }
            case blockLabelRecords.CALENDAR.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.CALENDAR, mimeType);
                break;
            }
            case blockLabelRecords.WEATHER.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.WEATHER, SceneMimeTypes.Player);
                break;
            }
            case blockLabelRecords.FIVEHUNDRED_COLLECTION.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FIVEHUNDRED_COLLECTION, mimeType);
                break;
            }
            case blockLabelRecords.FIVEHUNDRED_USER.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FIVEHUNDRED_USER, mimeType);
                break;
            }
            case blockLabelRecords.ETSY_LIST.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.ETSY_LIST, mimeType);
                break;
            }
            case blockLabelRecords.ETSY_SHOP.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.ETSY_SHOP, mimeType);
                break;
            }
            case blockLabelRecords.ETSY_USER.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.ETSY_USER, mimeType);
                break;
            }
            case blockLabelRecords.FACEBOOK_ALBUM.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FACEBOOK_ALBUM, mimeType);
                break;
            }
            case blockLabelRecords.FACEBOOK_WALL.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FACEBOOK_WALL, mimeType);
                break;
            }
            case blockLabelRecords.FACEBOOK_VIDEO.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FACEBOOK_VIDEO, mimeType);
                break;
            }
            case blockLabelRecords.TUMBLRPHOTOS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TUMBLRPHOTOS, mimeType);
                break;
            }
            case blockLabelRecords.TUMBLRPOSTS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TUMBLRPOSTS, mimeType);
                break;
            }
            case blockLabelRecords.TUMBLRTEXTS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TUMBLRTEXTS, mimeType);
                break;
            }
            case blockLabelRecords.TUMBLRVIDEOS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TUMBLRVIDEOS, mimeType);
                break;
            }
            case blockLabelRecords.PINTEREST_BOARD.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.PINTEREST_BOARD, mimeType);
                break;
            }
            case blockLabelRecords.PINTEREST_USER.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.PINTEREST_USER, mimeType);
                break;
            }
            case blockLabelRecords.YELP_INFO.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.YELP_INFO, mimeType);
                break;
            }
            case blockLabelRecords.YELP_REVIEWS.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.YELP_REVIEWS, mimeType);
                break;
            }
            // case blockLabelRecords.INSTAGRAM_MEDIA.id: {
            //     component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.INSTAGRAM_MEDIA, mimeType);
            //     break;
            // }
            // case blockLabelRecords.INSTAGRAM_FEED.id: {
            //     component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.INSTAGRAM_FEED, mimeType);
            //     break;
            // }
            case blockLabelRecords.TWITTER.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TWITTER, mimeType);
                break;
            }
            case blockLabelRecords.IFTTT.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.IFTTT, mimeType);
                break;
            }
            case blockLabelRecords.FLICKR.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FLICKR, mimeType);
                break;
            }
            case blockLabelRecords.FACEBOOK_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FACEBOOK_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.FACEBOOK_GROUPS_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.FACEBOOK_GROUPS_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.GOOGLE_BUSINESS_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.GOOGLE_BUSINESS_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.INSTAGRAM_AYRSHARE.id:
            case '6050': {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.INSTAGRAM_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.LINKEDIN_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.LINKEDIN_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.PINTEREST_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.PINTEREST_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.REDDIT_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.REDDIT_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.TELEGRAM_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TELEGRAM_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.TIKTOK_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.TIKTOK_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.X_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.X_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.YOUTUBE_AYRSHARE.id: {
                component = this.swapTemplates(blockLabelRecords.JSONITEM, blockLabelRecords.YOUTUBE_AYRSHARE, mimeType);
                break;
            }
            case blockLabelRecords.LABEL.id: {
                component = blockLabelRecords.LABEL;
                break;
            }
            case blockLabelRecords.BULB.id: {
                component = blockLabelRecords.BULB;
                break;
            }
            case blockLabelRecords.ALARM_SYS.id: {
                component = blockLabelRecords.ALARM_SYS;
                break;
            }
            case blockLabelRecords.GAUGE.id: {
                component = blockLabelRecords.GAUGE;
                break;
            }
            case blockLabelRecords.KNOB.id: {
                component = blockLabelRecords.KNOB;
                break;
            }
            case blockLabelRecords.TRIVIA.id: {
                component = blockLabelRecords.TRIVIA;
                break;
            }
            case blockLabelRecords.BEACON.id: {
                component = blockLabelRecords.BEACON;
                break;
            }
            case blockLabelRecords.STATION.id: {
                component = blockLabelRecords.STATION;
                break;
            }
            case blockLabelRecords.COLOR_CONTROL.id: {
                component = blockLabelRecords.COLOR_CONTROL;
                break;
            }
            case blockLabelRecords.LABELQUEUE.id: {
                component = blockLabelRecords.LABELQUEUE;
                break;
            }
            case blockLabelRecords.RICH_TEXT.id: {
                component = blockLabelRecords.RICH_TEXT;
                break;
            }
            case '3240': {
                // Migrated LEGACY RichText: edit it through the same Text block. It stays player 3240 (its
                // <Text> Flash blob is converted <-> htmlText in scene.service.openRichTextEditor) so the
                // legacy Flash player keeps rendering it. Never created new — only exists in migrated scenes.
                component = blockLabelRecords.RICH_TEXT;
                break;
            }
            case blockLabelRecords.FASTERQ.id: {
                component = blockLabelRecords.FASTERQ;
                break;
            }
            case blockLabelRecords.CART.id: {
                component = blockLabelRecords.CART;
                break;
            }
            case blockLabelRecords.KEYBOARD.id: {
                component = blockLabelRecords.KEYBOARD;
                break;
            }
            case blockLabelRecords.HTML.id: {
                component = blockLabelRecords.HTML;
                break;
            }
            case blockLabelRecords.GAMES.id: {
                component = blockLabelRecords.GAMES;
                break;
            }
            case blockLabelRecords.BROWSER.id: {
                component = blockLabelRecords.BROWSER;
                break;
            }
            case blockLabelRecords.LOCATION.id: {
                component = blockLabelRecords.LOCATION;
                break;
            }
            case blockLabelRecords.CAPTURE.id: {
                component = blockLabelRecords.CAPTURE;
                break;
            }
            case blockLabelRecords.BUTTON.id: {
                component = blockLabelRecords.BUTTON;
                break;
            }
            case blockLabelRecords.SWITCH.id: {
                component = blockLabelRecords.SWITCH;
                break;
            }
            case blockLabelRecords.STEPPER.id: {
                component = blockLabelRecords.STEPPER;
                break;
            }
            case blockLabelRecords.ADNET.id: {
                component = blockLabelRecords.ADNET;
                break;
            }
            case blockLabelRecords.FORM.id: {
                component = blockLabelRecords.FORM;
                break;
            }
            case blockLabelRecords.RADIO.id: {
                component = blockLabelRecords.RADIO;
                break;
            }
            case blockLabelRecords.CHECKBOX.id: {
                component = blockLabelRecords.CHECKBOX;
                break;
            }
            case blockLabelRecords.SLIDER.id: {
                component = blockLabelRecords.SLIDER;
                break;
            }
            case blockLabelRecords.INPUT.id: {
                component = blockLabelRecords.INPUT;
                break;
            }
            case blockLabelRecords.EXT_IMAGE.id: {
                component = blockLabelRecords.EXT_IMAGE;
                break;
            }
            case blockLabelRecords.EXT_VIDEO.id: {
                component = blockLabelRecords.EXT_VIDEO;
                break;
            }
            case blockLabelRecords.COUNTDOWN.id: {
                component = blockLabelRecords.COUNTDOWN;
                break;
            }
            case blockLabelRecords.RSS.id: {
                component = blockLabelRecords.RSS;
                break;
            }
            case blockLabelRecords.PODCAST.id: {
                component = blockLabelRecords.PODCAST;
                break;
            }
            case blockLabelRecords.CLOCK.id: {
                component = blockLabelRecords.CLOCK;
                break;
            }
            case blockLabelRecords.IMAGE.id: {
                component = blockLabelRecords.IMAGE;
                break;
            }
            case blockLabelRecords.SVG.id: {
                component = blockLabelRecords.SVG;
                break;
            }
            case blockLabelRecords.PLAYLIST.id: {
                component = blockLabelRecords.PLAYLIST;
                break;
            }
            case blockLabelRecords.MUSICLIST.id: {
                component = blockLabelRecords.MUSICLIST;
                break;
            }
            case blockLabelRecords.MUSIC.id: {
                component = blockLabelRecords.MUSIC;
                break;
            }
            case blockLabelRecords.FLICKING.id: {
                component = blockLabelRecords.FLICKING;
                break;
            }
            case blockLabelRecords.COLLECTION.id: {
                component = blockLabelRecords.COLLECTION;
                break;
            }
            case blockLabelRecords.LOADER.id: {
                component = blockLabelRecords.LOADER;
                break;
            }
            case blockLabelRecords.SCHEDULER.id: {
                component = blockLabelRecords.SCHEDULER;
                break;
            }
            case blockLabelRecords.HOURLYBALANCER.id: {
                component = blockLabelRecords.HOURLYBALANCER;
                break;
            }
            case blockLabelRecords.VIDEO.id: {
                component = blockLabelRecords.VIDEO;
                break;
            }
            case blockLabelRecords.YOUTUBE.id: {
                component = blockLabelRecords.YOUTUBE;
                break;
            }
            case blockLabelRecords.QR.id: {
                component = blockLabelRecords.QR;
                break;
            }
            case blockLabelRecords.CHAT.id: {
                component = blockLabelRecords.CHAT;
                break;
            }
            case blockLabelRecords.WEBCAM.id: {
                component = blockLabelRecords.WEBCAM;
                break;
            }
            case blockLabelRecords.CANVA.id: {
                component = blockLabelRecords.CANVA;
                break;
            }
            case blockLabelRecords.ICON.id: {
                component = blockLabelRecords.ICON;
                break;
            }
            case blockLabelRecords.CHART.id: {
                component = blockLabelRecords.CHART;
                break;
            }
            default: {
                component = blockLabelRecords.GENERIC;
                this.toastr.error(this.translocoService.translate("toast.componentNotSupported2", {}, this.translocoService.getActiveLang()));
                if (Lib.DevMode()) {
                    alert("be sure to look at add to scene-add-block-component.ts and search also createComponent(");
                }
            }
        }
        return component;
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // WINDOW & UI UTILITIES
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Listens to window resize events with debouncing
     *
     * @returns Observable of window dimensions {innerHeight, innerWidth}
     *
     * @remarks
     * - Debounced by 333ms to prevent excessive updates during resize
     * - Used for responsive canvas/scene editor adjustments
     */
    onWindowResize() {
        return fromEvent(window, "resize").pipe(
            debounceTime(333),
            map((e) => {
                return { innerHeight: window.innerHeight, innerWidth: window.innerWidth };
            }));
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // SCENE & PLAYER SELECTORS - Editor State
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Listens to ALL application state (entire store)
     *
     * @returns Observable<IAppState> - Complete application state
     *
     * @remarks
     * Use sparingly - prefer specific selectors for performance.
     * Useful for debugging or when multiple state slices are needed.
     */
    listenAllData(): Observable<IAppState> {
        return this.store.select(store => store);
    }

    /**
     * Listens to the currently selected scene ID in the editor
     *
     * @returns Observable<string> - Selected scene ID (e.g., '12345')
     *
     * @description
     * The scene ID represents which scene is currently open/active in the scene editor.
     * This is a UI state selector, not the scene data itself.
     *
     * @remarks
     * Emits every time user selects a different scene in the editor
     */
    listenSelectedSceneId(): Observable<string> {
        return this.store.select(store => store.config.uiSceneConfig.selectedSceneId);
    }

    /**
     * Synchronously resolve which scene AND item the NEXT undo/redo will modify, plus WHERE THE USER WAS DRILLED
     * TO when the edit was made, by reading the action stored on the ngrx-wieder history step about to be
     * applied. Returns { sceneId, sceneItemId, drill } (any of which may be null).
     *
     * ngrx-wieder is configured with trackActionPayload: true, so each step keeps the originating action(s).
     * We read the ids off that action (all tracked scene-edit actions carry them - directly, or under items[0]
     * for the group-update action, or itemIds/groupId for group/ungroup). UNDO looks at undoable[0], REDO at
     * undone[0]. sceneId is used instead of the immer patch's scenes[] array index because the array is reordered
     * on navigation, so the index would point at the wrong scene. Used by the editor to re-open the affected
     * scene AND select the reverted item so the change is actually visible - even when the edit lives in a scene
     * other than the one on screen (e.g. after drilling out).
     *
     * `drill` (IDrillPath, stamped by dispatch()) carries the full recorded position - the nested-scene crumbs AND
     * the in-scene group/component/playlist-page target. It is what lets undo re-enter a GROUP: a group edit
     * carries its PARENT scene's sceneId, so sceneId alone cannot tell "inside the group" from "at scene level".
     */
    getHistoryTarget(actionType: 'UNDO' | 'REDO'): { sceneId: Id, sceneItemId: Id, drill: IDrillPath | null } {
        let target: { sceneId: Id, sceneItemId: Id, drill: IDrillPath | null } = { sceneId: null, sceneItemId: null, drill: null };
        this.store.select(store => store.users).pipe(take(1)).subscribe((users: any) => {
            const history = users?.histories?.DEFAULT;
            if (!history) return;
            const step = actionType === 'UNDO' ? history.undoable?.[0] : history.undone?.[0];
            // actions[0] is the NEWEST action of the step: wieder prepends on merge (actions: [slimmed, last,
            // ...prev]), so for a merged run of actionUpdateSceneItem this is the last edit - and therefore the
            // drill path the user was at when the run ended, which is where they expect to land.
            const action: any = step?.actions?.[0];
            if (!action) return;
            target = {
                sceneId: action.sceneId ?? action.items?.[0]?.sceneId ?? null,
                sceneItemId: action.sceneItemId ?? action.items?.[0]?.sceneItemId ?? action.groupId ?? action.group?.$?.id ?? action.itemIds?.[0] ?? null,
                drill: action[DRILL_PATH_KEY] ?? null,
            };
        });
        return target;
    }

    /**
     * Listens to the selected scene item's location index path
     *
     * @returns Observable<Array<number>> - Array of nested indices (e.g., [0, 2, 1])
     *
     * @description
     * Tracks the hierarchical position of the selected block within nested scene structures.
     * Each number represents the index at that nesting level.
     *
     * @remarks
     * Used for navigating nested/embedded scenes and locating blocks in scene tree
     */
    listenSelectedSceneLocationIndex(): Observable<Array<number>> {
        return this.store.select(store => store.config.uiSceneConfig.sceneItemLocationIndexes);
    }

    /**
     * Listens to fonts that have been previewed/loaded
     *
     * @returns Observable<Array<string>> - Array of font family names
     *
     * @remarks
     * Tracks which custom fonts have been dynamically loaded for preview in the editor
     */
    listenFontsPreviewed(): Observable<Array<string>> {
        return this.store.select(store => store.config.uiSceneConfig.fontsPreviewed);
    }

    /**
     * Listens to the selected scene item's coordinate location
     *
     * @returns Observable<Array<number>> - [x, y] coordinates
     *
     * @remarks
     * Pixel coordinates of selected block on the canvas
     */
    listenSelectedSceneLocationCoords(): Observable<Array<number>> {
        return this.store.select(store => store.config.uiSceneConfig.sceneItemLocationCoords);
    }

    /**
     * Listens to the selected playlist item indices
     *
     * @returns Observable<Array<number>> - Playlist indices (filters out null)
     *
     * @description
     * When working with playlist blocks, tracks which item indices are selected.
     * Filters null values - only emits when valid playlist indices exist.
     */
    listenSelectedScenePlaylist(): Observable<Array<number>> {
        return this.store.select(store => store.config.uiSceneConfig.sceneItemPlaylistIndexes)
            .pipe(filter(v => v !== null));
    }

    /**
     * Listens to the currently selected scene item/block ID
     *
     * @returns Observable<string> - Scene item/block ID
     *
     * @description
     * The scene item ID represents which specific block/component is currently selected
     * within the active scene in the editor.
     *
     * @remarks
     * Emits every time user selects a different block on the canvas
     */
    listenSelectedSceneItemId(): Observable<string> {
        return this.store.select(store => store.config.uiSceneConfig.selectedSceneItemId);
    }

    /**
     * Listens to the last block duplication operation
     *
     * @returns Observable<IUIDuplicationBlock> - Duplication metadata
     *
     * @description
     * Tracks the most recent block/scene duplication with details about what was copied.
     * Used for implementing paste/duplicate operations.
     */
    listenDuplicateScene(): Observable<IUIDuplicationBlock> {
        return this.store.select(store => store.config.uiSceneConfig.lastDuplication);
    }

    /**
     * Listens to the last duplication command type
     *
     * @returns Observable<DuplicateCommand> - Command type (e.g., 'copy', 'cut')
     *
     * @remarks
     * Distinguishes between copy, cut, and other duplication operations
     */
    listenDuplicateSceneCommand(): Observable<DuplicateCommand> {
        return this.store.select(store => store.config.uiSceneConfig.lastDuplicationCommand);
    }

    /**
     * Listens to animation clipboard (for copy/paste animations)
     *
     * @returns Observable<IAnimationEngine> - Animation data in clipboard
     *
     * @description
     * Tracks copied animation configurations for paste operations.
     * Allows users to copy animation settings from one block to another.
     */
    listenSceneAnimationClipboard(): Observable<IAnimationEngine> {
        return this.store.select(store => store.config.uiSceneConfig.sceneAnimationClipboard);
    }

    /**
     * Listens to both selected scene ID and scene item ID (combined)
     *
     * @param alwaysReturn - If true, emits null values; if false, filters them out (default: false)
     * @returns Observable<ISelectedSceneSceneId> - Object with {sceneId, sceneItemId}
     *
     * @description
     * Combines two UI state selectors into a single Observable for convenience.
     * Useful when components need both the active scene and selected block simultaneously.
     *
     * @remarks
     * - Uses combineLatest to react to changes in either scene or item selection
     * - Filters null by default unless alwaysReturn=true
     * - Returns null if either sceneItemId or sceneId is empty
     *
     * @example
     * ```typescript
     * this.storeSelector.listenSelectedSceneIdAndSceneItemId()
     *     .subscribe(selection => {
     *         console.log('Scene:', selection.sceneId);
     *         console.log('Block:', selection.sceneItemId);
     *     });
     * ```
     */
    listenSelectedSceneIdAndSceneItemId(alwaysReturn = false): Observable<ISelectedSceneSceneId> {
        const selectedSceneId$ = this.store.select(store => store.config.uiSceneConfig.selectedSceneId);
        const selectedSceneItemId$ = this.store.select(store => store.config.uiSceneConfig.selectedSceneItemId);
        return combineLatest([selectedSceneItemId$, selectedSceneId$]).pipe(
            map(([sceneItemId, sceneId]) => {
                if (isNoValue(sceneItemId) || isNoValue(sceneId))
                    if (alwaysReturn) {
                        return {
                            sceneId: null,
                            sceneItemId: null,
                        };
                    } else {
                        return null;
                    }
                return {
                    sceneId,
                    sceneItemId,
                };
            }),
            filter(value => value !== null),
        );
    }

    /**
     * Listens to the complete data of the currently selected scene
     *
     * @returns Observable<IPlayerProps> - Full scene data object
     *
     * @description
     * Combines selected scene ID (from UI config) with scenes data (from store)
     * to return the complete IPlayerProps object for the active scene.
     *
     * @remarks
     * - Uses combineLatest to react to scene selection changes or scene data updates
     * - Returns null if scenes haven't loaded or scene not found
     * - Emits whenever selected scene changes or its data is updated
     *
     * @example
     * ```typescript
     * this.storeSelector.listenSelectedScene()
     *     .subscribe(scene => {
     *         if (scene) {
     *             console.log('Scene label:', scene.Player.$.label);
     *             console.log('Scene blocks:', scene.Player.Data[0].Scene[0].Players[0].Player);
     *         }
     *     });
     * ```
     */
    listenSelectedScene() {
        const selectedSceneId$ = this.store.select(store => store.config.uiSceneConfig.selectedSceneId);
        const scenes$ = this.store.select(store => store.users.scenes);
        return combineLatest([selectedSceneId$, scenes$]).pipe(
            map(([sceneId, i_scenes]) => {
                if (!_.isNull(i_scenes)) {
                    return i_scenes.find((i_scene: IPlayerProps) => i_scene.Player.$.id === sceneId);
                }
                return null;
            }),
        );
    }

    /**
     * Listens to the player data of the currently selected scene item/block
     *
     * @param alwaysReturn - If true, emits null objects; if false, filters them (default: false)
     * @returns Observable<ISceneTreeSliceResults> - Object with {sceneId, sceneItem (ISceneBlock)}
     *
     * @description
     * This is a CRITICAL selector that combines three store slices to return the actual
     * ISceneBlock data for the currently selected block in the editor. It navigates the
     * scene tree structure to find and return the selected block's complete data.
     *
     * @remarks
     * - Uses combineLatest on 3 Observables (selectedSceneItemId, selectedSceneId, scenes)
     * - Calls getPlayerIdInScene() utility to navigate scene tree structure
     * - Returns both sceneId and the sceneItem (block) data
     * - Essential for property panels and block editing components
     *
     * @example
     * ```typescript
     * this.storeSelector.listenPlayerDataOfSelectedSceneItemId()
     *     .subscribe(result => {
     *         const block: ISceneBlock = result.sceneItem;
     *         console.log('Block type:', block.$.player);
     *         console.log('Block data:', block.Data);
     *     });
     * ```
     */
    listenPlayerDataOfSelectedSceneItemId(alwaysReturn = false): Observable<ISceneTreeSliceResults> {
        const selectedSceneId$ = this.store.select(store => store.config.uiSceneConfig.selectedSceneId);
        const selectedSceneItemId$ = this.store.select(store => store.config.uiSceneConfig.selectedSceneItemId);
        const scenes$ = this.store.select(store => store.users.scenes);
        return combineLatest([selectedSceneItemId$, selectedSceneId$, scenes$]).pipe(
            map(([sceneItemId, sceneId, scenes]) => {
                if (isNoValue(sceneItemId) || isNoValue(sceneId) || isNoValue(scenes))
                    if (alwaysReturn) {
                        return { sceneId: null, sceneItem: null };
                    } else {
                        return null;
                    }
                return getPlayerIdInScene(scenes, sceneId, sceneItemId);
            }),
            filter(value => value !== null),
        );
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // SCENE & PLAYER SELECTORS - Scene Data
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Listens to all scenes in the user's account
     *
     * @returns Observable<IPlayerProps[]> - Array of all scenes (filters null/undefined)
     *
     * @description
     * Returns ALL scenes for the user - the primary selector for accessing scene data.
     * Filters out null/undefined to ensure scenes have loaded.
     *
     * @remarks
     * - This is the foundation selector for most scene-related operations
     * - Emits whenever scenes are updated (create, update, delete operations)
     * - Use specialized getScenes() for filtered scenes (remove problem scenes, private children, etc.)
     *
     * @example
     * ```typescript
     * this.storeSelector.listenScenes()
     *     .subscribe(scenes => {
     *         console.log('Total scenes:', scenes.length);
     *         scenes.forEach(scene => {
     *             console.log('Scene:', scene.Player.$.label);
     *         });
     *     });
     * ```
     */
    public listenScenes(): Observable<IPlayerProps[]> {
        return this.store.select(store => store.users.scenes)
            .pipe(filter((scenes) => !_.isUndefined(scenes) && !_.isNull(scenes)));
    }

    /**
     * Gets a scene ID by scene name (label)
     *
     * @param sceneName - Name/label of the scene to find
     * @returns Promise<string | null> - Scene ID if found, null otherwise
     *
     * @description
     * Searches all scenes for a matching label and returns the scene ID.
     * Useful for finding scenes by name for src references.
     *
     * @example
     * ```typescript
     * const sceneId = await this.storeSelector.getSceneIdByName('My Scene');
     * if (sceneId) {
     *     console.log('Found scene ID:', sceneId);
     * }
     * ```
     */
    public getSceneIdByName(sceneName: string): Promise<string | null> {
        return new Promise((resolve) => {
            this.listenScenes()
                .pipe(take(1))
                .subscribe((scenes: IPlayerProps[]) => {
                    const scene = scenes.find((s: IPlayerProps) =>
                        s.Player.$.label === sceneName
                    );
                    resolve(scene ? scene.Player.$.id : null);
                });
        });
    }

    /**
     * Checks if a scene with the given name exists
     *
     * @param sceneName - Name/label of the scene to check
     * @returns Promise<boolean> - True if scene exists, false otherwise
     *
     * @description
     * Checks if any scene has a matching label.
     * Case-sensitive comparison.
     *
     * @example
     * ```typescript
     * const exists = await this.storeSelector.isSceneNameExists('My Scene');
     * if (exists) {
     *     console.log('Scene already exists');
     * }
     * ```
     */
    public isSceneNameExists(sceneName: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.listenScenes()
                .pipe(take(1))
                .subscribe((scenes: IPlayerProps[]) => {
                    const exists = scenes.some((s: IPlayerProps) =>
                        s.Player.$.label === sceneName
                    );
                    resolve(exists);
                });
        });
    }

    public listenSceneImports(): Observable<ISceneTemplateImport[]> {
        return this.store.select(store => store.users.sceneTemplateImports)
            .pipe(filter((scenes) => !_.isNull(scenes)));
    }

    public listenPlayersOfScene(i_selectedSceneId): Observable<ISceneBlock[]> {
        return this.store.select(store => store.users.scenes).pipe(
            filter((i_scenes: IPlayerProps[]) => !_.isNull(i_scenes)),
            map((i_scenes: IPlayerProps[]) => {
                return i_scenes.find((i_scene: IPlayerProps) => {
                    return i_scene.Player.$.id == i_selectedSceneId;
                });
            }),
            map((i_scenePlayer: IPlayerProps) => {
                return (i_scenePlayer ? (i_scenePlayer.Player.Data["0"].Scene["0"].Players["0"].Player) : []) as ISceneBlock[];
            }));
    }

    public listenPrivateChildPlayersOfScene(i_selectedSceneId): Observable<ISceneBlock[]> {
        return this.store.select(store => store.users.scenes).pipe(
            filter((i_scenes: IPlayerProps[]) => !_.isNull(i_scenes)),
            map((i_scenes: IPlayerProps[]) => {
                return i_scenes.find((i_scene: IPlayerProps) => {
                    return i_scene.Player.$.id == i_selectedSceneId;
                });
            }),
            map((i_scenePlayer: IPlayerProps) => {
                const players = (i_scenePlayer ? (i_scenePlayer.Player.Data["0"].Scene["0"].Players["0"].Player) : []);
                if (!players) return [];
                return players.filter((i_player) => `${i_player.$.private_child}` === "true") as ISceneBlock[];
            }));
    }

    public listenSceneData(i_selectedSceneId): Observable<IPlayerProps> {
        return this.store.select(store => store.users.scenes).pipe(
            filter((i_scenes: IPlayerProps[]) => !_.isNull(i_scenes)),
            map((i_scenes: IPlayerProps[]) => {
                return i_scenes.find((i_scene: IPlayerProps) => {
                    return i_scene.Player.$.id === i_selectedSceneId;
                });
            }));
    }

    public listenMultiSceneData(i_selectedSceneIds = []): Observable<IPlayerProps[]> {
        return this.store.select(store => store.users.scenes).pipe(
            filter((i_scenes: IPlayerProps[]) => !_.isNull(i_scenes)),
            map((i_scenes: IPlayerProps[]) => {
                return i_scenes.filter((i_scene: IPlayerProps) => {
                    return i_selectedSceneIds.includes(i_scene.Player.$.id);
                });
            }));
    }

    public listenSceneOptions(): Observable<any> {
        return this.store.select(store => store.users.scenes)
            .pipe(
                filter((scenes) => !_.isUndefined(scenes) && !_.isNull(scenes)),
                map((i_scenes) => {
                    return i_scenes.map((i_scene: IPlayerProps) => ({
                        id: i_scene.Player.$.id,
                        label: i_scene.Player.$.label,
                        mimeType: i_scene.Player.$.mimeType,
                    }));
                }),
            );
    }

    /** For now, we do not use this function. Because, touch components can be layered at any position */
    public getPlayerIndexByTouch(i_selectedSceneId): Observable<number> {
        return this.store.select(store => store.users.scenes).pipe(
            filter((i_scenes: IPlayerProps[]) => !_.isNull(i_scenes)),
            map((i_scenes: IPlayerProps[]) => {
                return i_scenes.find((i_scene: IPlayerProps) => {
                    return i_scene.Player.$.id === i_selectedSceneId;
                });
            }),
            map((i_scenePlayer: IPlayerProps) => {
                if (!i_scenePlayer) return -1;
                if (!i_scenePlayer.Player.Data["0"]?.Scene["0"]?.Players["0"]?.Player) {
                    return -1;
                }
                return i_scenePlayer.Player.Data["0"].Scene["0"].Players["0"].Player.findIndex((_player) => {
                    /** 04-03-2022 ran into rare cases where no $ existed so just return false as this is not touch component **/
                    if (!_player.$) return false;
                    return this.getBlockLabelById(_player.$.player).touch;
                });
            }));
    }

    public getState<T>(slice, emitOnEmpty = true): Promise<List<T>> {
        let go = 1;
        return new Promise((res, err) => {
            this.store.select(store => store.adnet[slice])
                .pipe(
                    filter((v) => v !== null),
                    takeWhile((v) => go > 0),
                )
                .subscribe((d: List<T>) => {
                    go = 0;
                    if (emitOnEmpty === false && d.size === 0) return;
                    res(d);
                });
        });
    }

    public getLiveChatStatusV1(): Observable<number> {
        return this.store.select(store => store.config.chat.liveStatus);
    }

    public getLiveChatStatusV2(): Observable<IChatGlobal> {
        return this.store.select(store => store.config.chat);
    }

    public getPreloginResellerInfo(): Observable<IPreloadResellerInfo> {
        return this.store.select(store => store.users.preLoginResellerInfo)
            .pipe(filter((reseller) => reseller !== null));
    }

    public getAdnetCustomerCredentialsPromise(i_customerId: string): Promise<IAdnetCustomerCredentials> {
        let go = 1;
        return new Promise((res, err) => {
            return this.getEnterpriseBusinesses()
                .pipe(take(1),
                    map((b) => {
                        // tslint:disable-next-line:no-shadowed-variable
                        const res = b.Businesses.BusinessInfo.find((v) => {
                            return v.AdNet[0].$.customerId == i_customerId;
                        });
                        return {
                            adnetCustomerId: i_customerId,
                            adnetTokenId: res.AdNet[0].$.customerToken,
                            businessId: res.$.businessId,
                        };
                    }),
                    takeWhile((v) => go > 0))
                .subscribe((d: IAdnetCustomerCredentials) => {
                    go = 0;
                    res(d);
                });
        });
    }

    public getAdnetCustomerCredentials(i_customerId: string): Observable<IAdnetCustomerCredentials> {
        return this.getEnterpriseBusinesses()
            .pipe(take(1), map((b) => {
                const res = b.Businesses.BusinessInfo.find((v) => {
                    return v.AdNet[0].$.customerId == i_customerId;
                });
                return {
                    adnetCustomerId: i_customerId,
                    adnetTokenId: res.AdNet[0].$.customerToken,
                    businessId: res.$.businessId,
                };
            }));
    }

    public getAdnet(): Observable<IAdnetState> {
        return this.store.select(store => store.adnet);
    }

    public getAdnetRates(): Observable<List<AdnetRateModel>> {
        return this.store.select(store => store.adnet.rates);
    }

    public getAdnetPairs(): Observable<List<AdnetPairModel>> {
        return this.store.select(store => store.adnet.pairs).pipe(filter((user) => user !== null));
    }

    public getAdnetTransfers(): Observable<List<AdnetTransferModel>> {
        return this.store.select(store => store.adnet.transfers).pipe(filter((user) => user !== null));
    }

    public getAdnetPayments(): Observable<List<AdnetPaymentModel>> {
        return this.store.select(store => store.adnet.payments).pipe(filter((user) => user !== null));
    }

    public getAdnetReports(): Observable<List<AdnetReportModel>> {
        return this.store.select(store => store.adnet.reports).pipe(filter((user) => user !== null));
    }

    public getAdnetPackages(): Observable<List<AdnetPackageModel>> {
        return this.store.select(store => store.adnet.packages).pipe(filter((user) => user !== null));
    }

    public getAdnetTargets(): Observable<List<AdnetTargetModel>> {
        return this.store.select(store => store.adnet.targets).pipe(filter((user) => user !== null));
    }

    public getAdnetTargetsSearch(): Observable<List<AdnetTargetModel>> {
        return this.store.select(store => store.adnet.targets_search).pipe(filter((user) => user !== null));
    }

    public getAdnetCustomersNoFilter(): Observable<List<AdnetCustomerModel>> {
        return this.store.select(store => store.adnet.customers);
    }

    public getAdnetCustomers(): Observable<List<AdnetCustomerModel>> {
        return this.store.select(store => store.adnet.customers).pipe(filter((user) => user !== null));
    }

    public getLoginViewMode(): Observable<EnumLoginViewMode> {
        return this.store.select(store => store.config.appSettings.loginState);
    }

    public getOS(): Observable<IOS> {
        return this.store.select(store => store.config.os);
    }

    public isFirefox(): Observable<boolean> {
        return this.getOS()
            .pipe(map((os) => {
                    return os?.name1?.toLowerCase()?.indexOf("firefox") > -1 || os?.name2?.toLowerCase()?.indexOf("firefox") > -1;
                }),
            );
    }

    public getLockedRecords(): Observable<ILockedRecord> {
        return this.store.select(store => store.users.lockedRecord);
    }

    public getChangelistIds(): Observable<IChangeListIds> {
        const changelistIdSocket$ = this.store.select(store => store.users.lockedRecord);
        const changelistIdSync$ = this.store.select(store => store.users.changelistId);
        return combineLatest([changelistIdSocket$, changelistIdSync$]).pipe(
            map(([a, b]) => {
                return {
                    changelistIdSync: a?.changelistId ? a.changelistId : NONE_VALUE,
                    changelistIdSocket: b ? b : NONE_VALUE,
                };
            }),
        );
    }

    // public getChangelistSocketId(): Observable<number> {
    //     return this.store.select(store => store.users.changelistId).pipe(map((v) => {
    //         if (isNullOrUndef(v)) return NONE_VALUE;
    //         return v;
    //     }));
    // }

    /**
     * Gets the current authenticated user
     *
     * @param emitOnEmpty - If true, emits null values; if false, filters them (default: false)
     * @returns Observable<IUser> - Current user object
     *
     * @description
     * THE PRIMARY selector for accessing current user data. Returns the authenticated user
     * with all account details, business ID, privileges, and configuration.
     *
     * @remarks
     * - Filters null by default (waits for user to load after login)
     * - Use emitOnEmpty=true when you need to detect logout state
     * - User data includes: businessId, username, accessMask, dashboard status, privileges, etc.
     * - Cached internally in m_user for synchronous access
     *
     * @example
     * ```typescript
     * // Wait for user to load (typical usage)
     * this.storeSelector.getUser()
     *     .subscribe(user => {
     *         console.log('Business ID:', user.businessId);
     *         console.log('Is enterprise:', user.dashboard);
     *     });
     *
     * // Include null emissions (for detecting logout)
     * this.storeSelector.getUser(true)
     *     .subscribe(user => {
     *         if (!user) {
     *             console.log('User logged out');
     *         }
     *     });
     * ```
     */
    public getUser(emitOnEmpty?): Observable<IUser> {
        return this.store.select(store => store.users.selectedUser)
            .pipe(filter((user) => {
                    if (emitOnEmpty) return true;
                    return user !== null;
                }),
            );
    }

    public getUserAccountDetails(): Observable<IAccountDetails> {
        if (this.m_user?.dashboard) {
            return this.getEnterpriseAccDetails();
        } else {
            return this.getUserAccDetail();
        }
    }

    public getUserAccDetail(): Observable<IAccountDetails> {
        return this.store.select(store => store.users.userDetails).pipe(filter((data) => data !== null));
    }

    public getEnterpriseAccDetails(): Observable<IAccountDetails> {
        return this.store.select(store => store.enterprise.enterpriseAccount).pipe(filter((data) => data !== null));
    }

    public getStorageStatistics(): Observable<IResourceStatistic> {
        const resourceTypes = SUPPORTED_FILE_TYPES;
        return this.getResources().pipe(
            map((i_resource) => {
                let categories: Array<string> = [];
                const resourcesByType = {};
                resourceTypes.forEach((type) => {
                    resourcesByType[type] = 0;
                });
                let totalSize = 0;
                i_resource.forEach((s, idx) => {
                    if (s.tree_path) {
                        categories.push(s.tree_path);
                    }
                    totalSize += parseInt(s.resource_bytes_total, 0);
                    const res_type = s.resource_type.toLowerCase();

                    switch (res_type) {
                        case "flv": {
                            resourcesByType["flv"]++;
                            break;
                        }
                        case "m4v": {
                            resourcesByType["m4v"]++;
                            break;
                        }
                        case "mp4": {
                            resourcesByType["mp4"]++;
                            break;
                        }
                        case "jpeg":
                        case "jpg": {
                            resourcesByType["jpg"]++;
                            break;
                        }
                        case "png": {
                            resourcesByType["png"]++;
                            break;
                        }
                        case "gif": {
                            resourcesByType["gif"]++;
                            break;
                        }
                        case "svg": {
                            resourcesByType["svg"]++;
                            break;
                        }
                        case "swf": {
                            resourcesByType["swf"]++;
                            break;
                        }
                        default: {
                            resourcesByType["other"]++;
                            break;
                        }
                    }
                });
                categories = categories
                    .reduce((x, y) => x.includes(y) ? x : [...x, y], [])
                    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
                const resourceStatistics = Array.from(resourceTypes, key => ({
                    name: key,
                    value: resourcesByType[key],
                }));
                const dataStorageUsed = [
                    {
                        name: "used storage",
                        value: formatBytes(totalSize, 2),
                    },
                ];
                const dataStoragePercent = [
                    {
                        name: "free cloud storage",
                        value: 1024 * 1024 - Math.round(totalSize),
                    },
                    {
                        name: "used storage",
                        value: Math.round(totalSize),
                    },
                ];
                return { resourceStatistics, dataStoragePercent, dataStorageUsed, categories, resourcesByType };
            }), filter((v) => {
                return v.dataStorageUsed?.[0]?.value !== "0 Byte";
            }),
        );
    }

    public getResourceSharedLibrary(): Observable<ISharedResourceLibrary> {
        return this.store.select(store => store.users.resourceSharedLibrary)
            .pipe(
                filter((data) => data !== null),
            );
    }

    public getLocalStoreUpdates(filterBy?: ILocalStoreTypes): Observable<ILocalStorageUpdates> {
        return this.store.select(store => store.config.localStorageUpdates).pipe(filter((v) => {
            if (!filterBy) return true;
            if (!v) return true;
            return v?.latestEvent === filterBy;
        }));
    }

    public getCreateEnterprise(): Observable<ILoginForm> {
        return this.store.select(store => store.users.createCredentials).pipe(map((v) => ({
            username: v?.username ? v.username : "",
            password: v?.password ? v.password : "",
        })));
    }

    public getSocialUserServerReply(): Observable<ISocialServerReply> {
        return this.store.select(store => store.config.socialUserServerReply).pipe(filter((user) => user !== null));
    }

    public getSocialUser(emitOnEmpty: boolean = false): Observable<{ a: SocialUser, b: ISocialServerReply }> {
        const socialUser$ = this.store.select(store => store.config.socialUser);
        const socialUserServerReply$ = this.store.select(store => store.config.socialUserServerReply);
        return combineLatest([socialUser$, socialUserServerReply$]).pipe(
            map(([a, b]) => ({ a, b })),
            mergeMap(v => (v ? of(v) : (emitOnEmpty ? of(v) : empty()))),
        );
    }

    public getResellerId(i_localStore: LocalStoreService): Observable<number> {
        const resellerStaticId$ = this.store.select(store => store.config?.staticIndexHTML?.resellerStaticId);
        const resellerId$ = this.store.select(store => store.users.selectedUser?.resellerId);
        const localStoreUpdates$ = this.getLocalStoreUpdates("ILocalStorageConfig");
        return combineLatest([resellerStaticId$, resellerId$, localStoreUpdates$])
            .pipe(
                map(([resellerStaticId, resellerId, localStoreUpdates]) => {
                    if (localStoreUpdates) {
                        const isWhitelabel = i_localStore.getStorageValConfig("isWhitelabel");
                        if (isDigitNoEmpty(isWhitelabel) && isWhitelabel !== "0") {
                            return Number(isWhitelabel);
                        }
                    }
                    if (isDigitNoEmpty(resellerStaticId))
                        return Number(resellerStaticId);
                    if (isDigitNoEmpty(Number(resellerId)))
                        return Number(resellerId);
                    return DEFAULT_RESELLER_ID;
                }),
                distinctUntilChanged(),
            );
    }

    public getLocalStatic(): Observable<string> {
        return this.store.select(store => store.config?.staticIndexHTML?.language);
    }

    public getEnterpriseReseller(): Observable<IEnterpriseReseller> {
        return this.store.select(store => store.enterprise.enterpriseReseller).pipe(filter((data) => data !== null));
    }

    public getEnterpriseApps(): Observable<Array<IStoreApps>> {
        return this.store.select(store => store.enterprise.enterpriseReseller).pipe(
            filter((data) => data !== null),
            map((v) => v.User.BusinessInfo[0].Apps));
    }

    public getEnterpriseWhiteLabel(): Observable<Array<IWhiteLabel>> {
        return this.store.select(store => store.enterprise.enterpriseReseller).pipe(
            filter((data) => data !== null),
            map((v) => v.User.BusinessInfo[0].WhiteLabel));
    }

    public getEnterpriseThemes(): Observable<Array<any>> {
        return this.store.select(store => store.enterprise.enterpriseReseller).pipe(
            filter((data) => data !== null),
            map((v) => v.User.BusinessInfo[0].Themes));
    }

    public getEnterpriseInstalledApps(): Observable<Array<IInstalledApp>> {
        return this.store.select(store => store.enterprise.enterpriseReseller).pipe(
            filter((data) => data !== null),
            map((v) => v.User.BusinessInfo[0].InstalledApps));
    }

    public getOrder(): Observable<IOrderDetails> {
        return this.store.select(store => store.users.order).pipe(filter((data) => data !== null));
    }

    public getOrders(): Observable<Array<IOrderForm>> {
        return this.store.select(store => store.users.orders).pipe(filter((data) => data !== null));
    }

    public getDomainBusinesses(): Observable<Array<string>> {
        const domains = [];
        return this.store.select(store => store.enterprise.enterpriseBusiness).pipe(map((v) => {
            v.Businesses.BusinessInfo.forEach((b: IBusinessAccount) => {
                if (domains.indexOf(b.$.domain) === -1) {
                    domains.push(b.$.domain);
                }
            });
            return domains;
        }));
    }

    public getBusinessesByDomain(): Observable<IBusinessDomainCollection> {
        const domains = {};
        return this.store.select(store => store.enterprise.enterpriseBusiness).pipe(map((v) => {
            if (v) {
                if (isNullOrUndefOrEmpty(v.Businesses.BusinessInfo)) {
                    return [];
                }
                v.Businesses.BusinessInfo.forEach((b: IBusinessAccount) => {
                    const domain = b.$.domain;
                    if (!domains[domain]) domains[domain] = [];
                    domains[domain].push(b.$.businessId);
                });
            }
            return domains;
        }));
    }

    public getEventParams(): Observable<Array<IEventParam>> {
        return this.store.select(appStore => appStore.users.eventParams).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    /** Cross-station event scope registry (event_scopes table). */
    public getEventScopes(): Observable<Array<IEventScope>> {
        return this.store.select(appStore => appStore.users.eventScopes).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getChartEventParams(): Observable<Array<IEventParam>> {
        return this.store.select(appStore => appStore.users.eventParams).pipe(
            filter(data => data != null),
            map(data => data.filter((v) => v.event_type === 3))
        );
    }

    public getEventStrings(): Observable<Array<string>> {
        return this.store.select(appStore => appStore.users.eventsStrings).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getEventBlocks(): Observable<Array<IEventBlock>> {
        return this.store.select(appStore => appStore.users.eventBlocks).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getIots(): Observable<Array<IIot>> {
        return this.store.select(appStore => appStore.users.iots).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getEmpty(): Observable<Array<string>> {
        return of[""];
    }

    public getEventsCombined(): Observable<Array<string>> {
        const eventsStrings$ = this.store.select(store => store.users.eventsStrings);
        const eventParams$ = this.store.select(store => store.users.eventParams);
        return combineLatest([eventsStrings$, eventParams$]).pipe(
            map(([a, b]) => {
                const z = [];
                b.forEach((c) => {
                    z.push(c.event_name);
                });
                return [...new Set([...a, ...z])];
            }),
        );
    }

    public getDynawebsOrder(): Observable<IOrderForm> {
        return this.store.select(appStore => appStore.users.dynawebs.order).pipe(filter(data => data != null));
    }

    public getDynawebsServer(): Observable<IDynawebsServerReply> {
        return this.store.select(appStore => appStore.users.dynawebs.dynawebsServer).pipe(filter(data => data != null));
    }

    public getDynawebsCart(): Observable<ICart> {
        return this.store.select(appStore => appStore.users.dynawebs.cart).pipe(filter(data => data != null));
    }

    public getDynawebsCartProducts(): Observable<Array<ICartProduct>> {
        return this.store.select(appStore => appStore.users.dynawebs.products).pipe(filter(data => data != null));
    }

    public getCartProducts(): Observable<Array<ICartProduct>> {
        return this.store.select(appStore => appStore.users.cart.products).pipe(filter(data => data != null));
    }

    public getCartProduct(name): Observable<ICartProduct> {
        return this.getCartProducts().pipe(map(v => {
            return v.find((b) => {
                return b.name === name;
            });
        }));
    }

    public getCartContents(): Observable<Array<ICartProduct>> {
        return this.store.select(appStore => appStore.users.cart.cartContent).pipe(filter(data => data != null));
    }

    public getCart(): Observable<ICart> {
        return this.store.select(appStore => appStore.users.cart).pipe(filter(data => data != null));
    }

    public getEnterpriseData(): Observable<object> {
        const enterpriseBusinessUsers$ = this.store.select(store => store.enterprise.enterpriseBusinessUsers);
        const enterpriseBusiness$ = this.store.select(store => store.enterprise.enterpriseBusiness);
        const enterpriseReseller$ = this.store.select(store => store.enterprise.enterpriseReseller);
        return combineLatest([enterpriseBusiness$, enterpriseReseller$, enterpriseBusinessUsers$]).pipe(
            map(([a, b, c]) => {
                if (isNullOrUndefOrEmpty(c))
                    c = [] as any;
                return { a, b, c };
            }),
            filter((values: any) => {
                return values.a && values.b && values.c;
            }),
        );
    }


    public getEnterpriseAccountsList(): Observable<List<BusinessModel>> {
        const businesses = [];
        return this.store.select(store => store.enterprise.enterpriseBusiness).pipe(map(b => {
            b.Businesses.BusinessInfo.forEach((i_business) => {
                const business: BusinessModel = new BusinessModel({
                    businessId: i_business.$.businessId,
                    source: i_business.$.domain,
                    name: i_business.$.name,
                    accountStatus: i_business.$.accountStatus,
                    applicationId: i_business.$.applicationId,
                    archiveState: i_business.$.archiveState,
                    fromTemplateId: i_business.$.fromTemplateId,
                    maxMonitors: i_business.$.maxMonitors,
                    maxDataStorage: i_business.$.maxDataStorage,
                    allowSharing: i_business.$.allowSharing,
                    studioLite: i_business.$.studioLite,
                    lastLogin: i_business.$.lastLogin,
                    resellerId: i_business.$.resellerId,
                    businessDescription: i_business.$.businessDescription,
                    adnetToken: i_business.AdNet["0"].$.customerToken,
                    adnetCustomerId: i_business.AdNet["0"].$.customerId,
                });
                businesses.push(business);
            });
            return List<any>(businesses);
        }));
    }

    public getEnterprisePrivileges(): Observable<Array<IPrivileges>> {
        return this.store.select(store => store.enterprise.enterpriseReseller)
            .pipe(
                filter((data) => data !== null),
                map((v) => {
                    return v.User.BusinessInfo[0].Privileges[0].Privilege;
                }),
            );
    }

    public getEnterpriseBusinesses(): Observable<IEnterpriseBusinesses> {
        return this.store.select(store => store.enterprise.enterpriseBusiness).pipe(filter((data) => data !== null));
    }

    public getEnterpriseBusinessUsers(): Observable<IEnterpriseBusinessUsers> {
        return this.store.select(store => store.enterprise.enterpriseBusinessUsers).pipe(filter((data) => data !== null));
    }

    public getAppSettings(): Observable<IAppStatic> {
        return this.store.select(store => store.config.appSettings);
    }

    // public getStationTracking(): Observable<IStationTrackReply[]> {
    //     if (this.m_user?.dashboard) {
    //         return this.store.select(appStore => appStore.enterprise.enterpriseStationTrack);
    //     } else {
    //         return this.store.select(appStore => appStore.users.userStationTrack);
    //     }
    // }

    // public getTrackStats(): Observable<IStationTrackReply[]> {
    //     // return combineLatest([this.getUser(), this.listenAllData()]).pipe(map(([a, b]) => {
    //     //     if (a.dashboard) {
    //     //         return b.enterprise.enterpriseStationTrackStats;
    //     //     } else {
    //     //         return b.users.userStationTrackStats;
    //     //     }
    //     // }))
    //     if (this.m_user?.dashboard) {
    //         return this.store.select(appStore => appStore.enterprise.enterpriseStationTrackStats).pipe(
    //             filter((data) => data !== null)
    //         );
    //     } else {
    //         return this.store.select(appStore => appStore.users.userStationTrackStats).pipe(
    //             filter((data) => data !== null)
    //         );
    //     }
    // }

    // public getEnterpriseTrackState(): Observable<IStationTrackReply[]> {
    //     return this.store.select((state) => state.enterprise.enterpriseStationTrackStats).pipe(filter((data) => data !== null));
    // }

    public getCoreData(): Observable<IAppSettingsWrap> {
        return combineLatest([this.getUser(), this.getAppSettings()]).pipe(map(([a, b]) => {
            return [a, b];
        }));
    }

    public getTransitionItems(): Observable<Array<ITransitionPoolItem>> {
        return this.store.select(appStore => appStore.users.transitionPoolItems).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getTransitionPools(): Observable<Array<ITransitionPool>> {
        return this.store.select(appStore => appStore.users.transitionPools).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getMusicSongs(): Observable<Array<IMusicChannelSong>> {
        return this.store.select(appStore => appStore.users.musicChannelSongs).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getMusicChannels(): Observable<Array<IMusicChannel>> {
        return this.store.select(appStore => appStore.users.musicChannels).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getScheduleEvents(): Observable<Array<IScheduleEvent>> {
        return this.store.select(appStore => appStore.users.scheduleEvents).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getScheduleEventsBySchedule(schedule_id): Observable<Array<IScheduleEvent>> {
        return this.store.select(appStore => appStore.users.scheduleEvents).pipe(
            filter(data => data != null),
            map((v) => {
                return v.filter((e) => e.schedule_id === schedule_id);
            }),
        );
    }

    /**
     * Retrieves schedule events filtered by source type and specific source IDs
     *
     * @description
     * Queries the NgRx store for schedule events that match a specific source type
     * (either "scene" or "resource") and belong to one of the provided source IDs.
     * This is useful for finding all scheduled playback events for specific scenes
     * or resources across all schedules.
     *
     * The method parses the event_value JSON field to extract the type and id,
     * then filters events matching both the source type and ID list.
     *
     * @param event_source - Type of event source to filter: "scene" for scene events, "resource" for resource events
     * @param sourceIds - Array of source IDs to match against (scene_ids or resource_ids)
     *
     * @returns Observable emitting an array of IScheduleEvent objects matching the criteria
     *
     * @example
     * // Get all schedule events for specific scenes
     * this.storeSelectors.getScheduleEventsBySource('scene', ['12345', '67890'])
     *   .subscribe(events => {
     *     console.log('Found schedule events for these scenes:', events);
     *   });
     *
     * @example
     * // Get all schedule events for specific resources
     * this.storeSelectors.getScheduleEventsBySource('resource', ['res_123', 'res_456'])
     *   .subscribe(events => {
     *     console.log('Found schedule events for these resources:', events);
     *   });
     */
    public getScheduleEventsBySource(event_source: "scene" | "resource", sourceIds: Array<Id>): Observable<Array<IScheduleEvent>> {
        return this.store.select(appStore => appStore.users.scheduleEvents).pipe(
            filter(data => data != null),
            map((v) => {
                return v.filter((e) => {
                    const eventValue = e.event_value ? JSON.parse(e.event_value) : {};
                    return eventValue.type === event_source && sourceIds.includes(String(eventValue.id));
                });
            }),
        );
    }

    public getSchedules(): Observable<Array<ISchedule>> {
        return this.store.select(appStore => appStore.users.schedules).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getSelectedSchedule(): Observable<ISchedule> {
        const selectedId$ = this.store.select(store => store.config.scheduleSelectionState.scheduler);
        const schedules$ = this.store.select(store => store.users.schedules);
        return combineLatest([selectedId$, schedules$]).pipe(
            map(([schedule, i_schedules]) => {
                if (!_.isNull(i_schedules)) {
                    return i_schedules.find((i_schedule: ISchedule) => i_schedule.schedule_id === schedule.scheduleId);
                }
                return null;
            }),
        );
    }

    /** if filterByKey return if any found matches, otherwise return entire global settings if one exists **/
    public getGlobalSettings(filterByKey?: IGlobalSettingKey): Observable<IGlobalSettings[]> {
        return this.store.select(appStore => appStore.users.globalSettings)
            .pipe(
                filter((data) => !(isNullOrUndefined(data) || data.length === 0)),
                map((v) => {
                    return v.filter((d) => {
                        if (!filterByKey) return true;
                        return d?.param_key === filterByKey;
                    });
                }),
                filter((data) => {
                    return data && data.length > 0;
                }),
            );
    }

    public getPreviousLinkedScene(): Observable<string> {
        return this.store.select(appStore => appStore.config.uiSceneConfig.previousLinkedSceneId).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getPreviousSlidesSceneId(): Observable<string> {
        return this.store.select(appStore => appStore.config.uiSceneConfig.previousSlidesSceneId).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getSceneBreadcrumbs(): Observable<Array<ISceneBreadcrumb>> {
        return this.store.select(store => store.config.uiSceneConfig.breadcrumbs);
    }

    public listenSelectedSchedule(): Observable<IScheduleSelection> {
        return this.store.select(store => store.config.scheduleSelectionState);
    }

    public getCatalogItems(): Observable<Array<ICatalogItems>> {
        return this.store.select(appStore => appStore.users.catalogItems).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getCatalogItemCategories(): Observable<Array<ICatalogItemCategories>> {
        return this.store.select(appStore => appStore.users.catalogItemCategories).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getCatalogValues(): Observable<Array<ICategoryValues>> {
        return this.store.select(appStore => appStore.users.categoryValues).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getCatalogResources(): Observable<Array<ICatalogItemResources>> {
        return this.store.select(appStore => appStore.users.catalogItemResources).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getCatalogInfos(): Observable<Array<ICatalogItemInfos>> {
        return this.store.select(appStore => appStore.users.catalogItemInfos).pipe(
            filter(data => data != null),
            map((v) => v),
        );
    }

    public getResource(i_resourceId): Observable<IResource> {
        return this.store.select(appStore => appStore.users.resources)
            .pipe(
                filter(data => data != null),
                map((v) => {
                    return v[v.findIndex((a) => a.resource_id == i_resourceId)];
                }),
            );
    }

    /**
     * Resolves the CONVERTED resource's type for a migrated SWF/FLV original. A migrated source keeps its own
     * id in the scene (so the legacy player renders it) but carries converted_resource_id -> the png/webm/mp4
     * the new player renders. Returns that converted resource's resource_type (lowercased), or '' when the
     * resource is not a migrated source. Lets callers show the Image vs Video editor/header for the block.
     */
    public getConvertedResourceType(i_resourceId): Observable<string> {
        return this.getResource(i_resourceId).pipe(
            take(1),
            mergeMap((res: any) => {
                const convId = res && res.converted_resource_id;
                if (convId == null) return of("");
                return this.getResource(String(convId)).pipe(
                    take(1),
                    map((conv: any) => String((conv && conv.resource_type) || "").toLowerCase())
                );
            })
        );
    }

    // public async crudRecord(saveRecord: ICRUDRecord): Promise<ICRUDRecord> {
    //     return new Promise((res, err) => {
    //         this.userService.crudRecord(saveRecord)
    //             .pipe(
    //                 map((i_reply: ICRUDRecordReply) => {
    //                     saveRecord.command = "reply";
    //                     saveRecord.serverReply = i_reply;
    //                     return res(saveRecord);
    //                 }),
    //                 catchError((i_error) => {
    //                     console.log(`actionCRUDRecordFailure ${i_error.message}`);
    //                     saveRecord.command = "reply";
    //                     saveRecord.serverReply = {id: null, status: false};
    //                     err(saveRecord);
    //                     return of(actionCRUDRecordFailure({saveRecord: {studioId: null, command: "reply", body: i_error, serverReply: {status: false, id: null}}}));
    //                 })
    //             ).subscribe();
    //     });
    // }


    public async getResourceById(i_resourceId): Promise<IResource> {
        return new Promise((res, err) => {
            this.store.select(appStore => appStore.users.resources)
                .pipe(
                    filter(data => data != null),
                    map((v) => {
                        return v[v.findIndex((a) => a.resource_id == i_resourceId)];
                    }),
                )
                .subscribe((v: IResource) => {
                    return res(v);
                });
        });
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // RESOURCE MANAGEMENT SELECTORS
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Gets all resources from the user's media library
     *
     * @param skipZeroSize - If true, filters out invalid/empty resources (default: true)
     * @returns Observable<IResource[]> - Array of resource objects
     *
     * @description
     * Returns the complete media library - all uploaded images, videos, SVGs, and other files.
     * By default, filters out placeholder/invalid resources with zero size.
     *
     * @remarks
     * - Primary selector for accessing the resource library
     * - Filters null values (waits for resources to load)
     * - Default skipZeroSize=true removes resources with:
     *   - resource_bytes_total = "0" or empty
     *   - resource_name with zero length
     * - Resources include: images, videos, SVG files, audio, documents, etc.
     * - Each resource has: resource_id, resource_name, resource_type, resource_bytes_total, etc.
     *
     * @example
     * ```typescript
     * // Get valid resources only (default)
     * this.storeSelector.getResources()
     *     .subscribe(resources => {
     *         console.log('Total files:', resources.length);
     *         resources.forEach(res => {
     *             console.log(res.resource_name, res.resource_type);
     *         });
     *     });
     *
     * // Get ALL resources including invalid ones
     * this.storeSelector.getResources(false)
     *     .subscribe(resources => {
     *         console.log('All resources:', resources.length);
     *     });
     * ```
     */
    public getResources(skipZeroSize = true): Observable<IResource[]> {
        return this.store.select(appStore => appStore.users.resources)
            .pipe(
                filter((data) => data !== null),
                map((v) => {
                    if (skipZeroSize === false) return v;
                    return v.filter((c: IResource) => {
                        return !(!c || c.resource_bytes_total === "0" || c.resource_bytes_total === "" || c.resource_name?.length === 0);
                    });
                    // if (c?.resource_bytes_total === '0' && c?.resource_name?.length === 0) {
                    //     Lib.DevShow('>>>>>>>> skipping invalid resource: ' + c?.resource_id);
                    //     return false;
                    // }
                }),
            );
    }

    // public getResources(): Observable<IResource[]> {
    //     return this.store.select(appStore => appStore.users.resources)
    //         .pipe(filter((data) => data !== null));
    // }

    public listenResourceById(i_resourceId): Observable<IResource> {
        return this.store.select(appStore => appStore.users.resources)
            .pipe(
                filter(data => data != null),
                map((v) => {
                    return v[v.findIndex((a) => a.resource_id == i_resourceId)];
                }),
            );
    }

    // public getPrivileges(): Observable<IPrivileges[]> {
    //     return this.store.select(appStore => appStore.enterprise.enterpriseReseller.User.BusinessInfo[0].Privileges)
    //         .map(v => v);
    // }

    public getScenes(i_removeProblemScenes: boolean = false, includePrivateChild: boolean = false, onlyPrivateChild: boolean = false, excludeTouch: boolean = false): Observable<IPlayerProps[]> {
        return this.store.select(appStore => appStore.users.scenes).pipe(
            filter(data => data != null),
            map(data => {
                if (!excludeTouch || isHostLocal()) return data;
                return data.filter((v) => {
                    return !isMainTouchScene(v);
                });
            }),
            map(data => {
                if (onlyPrivateChild) {
                    return data.filter((v) => {
                        return `${v?.Player?.$?.private_child}` === "true"; /** if private_child does exist but valued at false, also include it in array **/
                    });
                }
                if (includePrivateChild) {
                    return data;
                }
                return data.filter((v) => {
                    return !isTruthy(v?.Player?.$?.private_child); /** if private_child does exist but valued at false, also include it in array **/
                });
            }),
            map(data => {
                if (!i_removeProblemScenes) return data;
                return data.filter((v) => {
                    // if (isSceneDesktopEmpty(v)) return false; // removed 9-27-2025
                    return !isSceneEmbedded(v);
                });
            }));
    }

    public getTouchScenes(): Observable<IPlayerProps[]> {
        return this.store.select(appStore => appStore.users.scenes).pipe(
            filter(data => data != null),
            map(data => {
                return data.filter(isMainTouchScene);
            }));
    }

    public getTrashScenes(): Observable<IPlayerProps[]> {
        return this.store.select(appStore => appStore?.users?.trash).pipe(
            map((data) => Array.isArray(data) ? data : []),
        );
    }

    public getTrashResources(): Observable<IResource[]> {
        return this.store.select(appStore => appStore?.users?.trashResources).pipe(
            map((data) => Array.isArray(data) ? data : []),
        );
    }

    public getLastGetTime(): Observable<number> {
        return this.store.select(appStore => appStore.users.lastGetTime).pipe(
            filter(data => data != null),
            map(v => v));
    }


    public getSceneMimeType(i_sceneId): Observable<string> {
        return this.store.select(appStore => appStore.users.scenes)
            .pipe(
                take(1),
                filter((data) => data !== null),
                map((a) => {
                    const scene = a[a.findIndex((i_scene) => i_scene.Player.$.id == i_sceneId)];
                    return scene.Player.$.mimeType;
                }),
            );
    }

    public getScene(i_sceneId): Observable<IPlayerProps> {
        return this.store.select(appStore => appStore.users.scenes).pipe(
            filter(data => data != null),
            map((v) => {
                return v[v.findIndex((i_scene) => i_scene.Player.$.id == i_sceneId)];
            }),
        );
    }

    public isFullFeatures(): Observable<boolean> {
        return this.store.select(appStore => appStore.config.appSettings.isFullFeatures);
    }

    public getSceneMime(i_sceneId): Observable<string> {
        return this.store.select(appStore => appStore.users.scenes).pipe(
            take(1),
            filter((data) => data !== null),
            map((v) => {
                const scene = v[v.findIndex((i_scene) => i_scene.Player.$.id == i_sceneId)];
                return scene.Player.$.mimeType;
            }),
        );
    }

    public getStationStats(): Observable<IStationStatsV1[]> {
        if (this.m_user && this.m_user.dashboard) {
            return this.store.select(appStore => appStore.enterprise.enterpriseStations).pipe(
                filter((data) => data !== null),
                // ,mergeMap(a => a)
            );
        } else {
            return this.store.select(appStore => appStore.users.stationStats)
                .pipe(
                    filter((data) => {
                        return data !== null;
                    }),
                    map((data) => {
                        return data;
                    }),
                    // ,mergeMap(a => a)
                );
        }
    }

    public getStation(businessStationId): Observable<IStation> {
        return this.store.select(appStore => appStore.users.stations)
            .pipe(map((v) => {
                    return v.find((d) => {
                        return d.station_business_id === businessStationId;
                    });
                }),
            );
    }

    public getStations(emitOnEmpty?): Observable<IStation[]> {
        return this.store.select(appStore => appStore.users.stations)
            .pipe(map(v => v),
                filter(v => {
                    if (emitOnEmpty) return true;
                    return v !== null;
                }),
            );
    }

    public getEnterpriseStations(): Observable<IStationStatsV1[]> {
        return this.store.select(appStore => appStore.enterprise.enterpriseStations)
            .pipe(
                map(v => v ? v : []),
                // ,
                // filter(v => v !== null)
            );
    }

    // station_business_id values we've already warned about colliding in stationStats — so the
    // diagnostic below logs ONCE per distinct collision instead of on every (frequent) selector
    // emission. Lives for the session; a page reload recreates the service and resets it.
    private m_warnedDuplicateStationKeys = new Set<string>();

    public getStationsComplete(connected: EnumConnectionType = EnumConnectionType.ALL, filterBy: EnumStationFilterType = EnumStationFilterType.NONE, i_station_ids_or_business_ids: Array<Id> = [], stationVersion: Id = null): Observable<Array<IStationData>> {
        const stations$ = this.m_user?.dashboard ? of([]) : this.getStations(true);
        const stationGeoIPs$ = this.getStationGeoIPs();
        const stationStats$ = this.getStationStats();
        return combineLatest([stations$, stationStats$, stationGeoIPs$]).pipe(map(([stations, stationStats, stationGeos]) => {
            let stationsComplete: Array<IStationData> = [];
            // Guard against duplicate station_business_id rows in stationStats. The grid's trackBy,
            // selection model and the join below all key on station_business_id, so two rows sharing a
            // key make the grid ALIAS them — selecting one selects both, and one station shows the
            // other's name (seen Jun 2026 with an RPi + Ubuntu station). This should never happen, so
            // when it does: warn ONCE per colliding key (the selector re-emits on every status tick —
            // unguarded this would flood the console) with the offending rows so the cause is
            // diagnosable, then drop the duplicate (first wins, matching the join's existing semantics)
            // so the grid stays correct instead of breaking.
            const seenKeys = new Set<string>();
            const dedupedStats: Array<IStationStatsV1> = [];
            stationStats.forEach((s) => {
                const k = String(s?.$?.station_business_id);
                if (seenKeys.has(k)) {
                    if (!this.m_warnedDuplicateStationKeys.has(k)) {
                        this.m_warnedDuplicateStationKeys.add(k);
                        const kept = dedupedStats.find((d) => String(d?.$?.station_business_id) === k);
                        console.warn(`⚠️ duplicate station_business_id "${k}" in stationStats — grid would alias two stations. Keeping first, dropping duplicate.`, {
                            kept: { id: kept?.$?.id, version: kept?.$?.version, name: kept?.$?.name, connection: kept?.$?.connection },
                            dropped: { id: s?.$?.id, version: s?.$?.version, name: s?.$?.name, connection: s?.$?.connection },
                        });
                    }
                    return; // drop the duplicate so the grid doesn't alias
                }
                seenKeys.add(k);
                dedupedStats.push(s);
            });
            stationStats = dedupedStats;
            if (this.m_user?.dashboard) {
                stations = [];
                stationStats.forEach((s) => {
                    stations.push({
                        station_business_id: s.$.station_business_id,
                        station_id: s.$.id,
                        station_name: s.$.name,
                        tree_path: s.$.tree_path,
                    });
                });
            }
            // /** if we found new stations that were just registered **/
            // if (stationStats?.length > 0 && stationStats?.length > stations?.length) {
            //     this.getChangelistIds()
            //         .pipe(
            //             debounceTime(3000),
            //             take(1))
            //         .subscribe((i_changelistIds) => {
            //             this.dispatchStaggered([
            //                 actionLoadAccountRequest({businessId: this.m_user.businessId, changelistId: i_changelistIds.changelistIdSync}),
            //                 actionLoadStationRequest({businessId: this.m_user.businessId})
            //             ], null, 2000);
            //         });
            // }
            if (!stations || stations.length === 0) return [];
            /** Index stats and geos by station_business_id ONCE so the join below is O(n) instead of
             *  O(n squared). With enterprise dashboards loading 700+ stations the old per-station
             *  findIndex (1.1M comparisons per emission) was the main rendering bottleneck. Keep the
             *  FIRST match (has() guard) to exactly preserve the previous findIndex semantics. **/
            const statByKey = new Map<string, IStationStatsV1>();
            stationStats.forEach((item) => {
                const k = String(item?.$?.station_business_id);
                if (!statByKey.has(k)) statByKey.set(k, item);
            });
            const geoByKey = new Map<string, IStationGeoIp>();
            stationGeos.forEach((item) => {
                const k = String(item?.station_business_id);
                if (!geoByKey.has(k)) geoByKey.set(k, item);
            });
            stations.forEach((i_station) => {
                const stationData: IStationData = { stat: null, props: null, geo: null };
                stationData.props = i_station;
                const k = String(i_station.station_business_id);
                const stat = statByKey.get(k);
                if (stat) stationData.stat = stat;
                const geo = geoByKey.get(k);
                if (geo) stationData.geo = geo;
                stationsComplete.push(stationData);
            });
            if (stationVersion == "1" || stationVersion == "2") {
                stationsComplete = stationsComplete.filter((v) => {
                    if (stationVersion == "2") {
                        return v?.stat?.$?.version == stationVersion;
                    } else {
                        return v?.stat?.$?.version != "2";
                    }
                });
            }
            // if (filterBy == "NONE") doNothing();
            if (filterBy == EnumStationFilterType.BUSINESSES)
                stationsComplete = stationsComplete.filter((v) => i_station_ids_or_business_ids.indexOf(v.stat.$.businessId) > -1);
            if (filterBy == EnumStationFilterType.STATIONS)
                stationsComplete = stationsComplete.filter((v) => i_station_ids_or_business_ids.indexOf(v.stat.$.station_business_id) > -1);
            // if (connected == "ALL") doNothing();
            if (connected == EnumConnectionType.CONNECTED)
                stationsComplete = stationsComplete.filter((v) => {
                    if (v?.stat?.$?.version == "2") {
                        return v?.stat?.$?.connection == "1";
                    } else {
                        if (v?.stat?.$?.socket != "0") return true;
                        if (v?.stat?.$?.socket == "0" && v?.stat?.$?.connection == "2") return true; // polling mode
                        return false;
                    }
                });
            if (connected == EnumConnectionType.DISCONNECTED)
                stationsComplete = stationsComplete.filter((v) => {
                    if (v?.stat?.$?.version == "2") {
                        return v?.stat?.$?.connection !== "1";
                    } else {
                        if (v?.stat?.$?.socket == "0" && v?.stat?.$?.connection == "2") return false; // polling mode
                        return v?.stat?.$?.socket == "0";
                    }
                });
            return stationsComplete;
        }));
    }

    public getStationGeoIPs(): Observable<IStationGeoIp[]> {
        return this.getUser().pipe(
            map((i_user) => {
                if (i_user.dashboard) {
                    return this.store.select(appStore => appStore.enterprise.enterpriseStationGeoIPs).pipe(filter((data) => data !== null));
                } else {
                    return this.store.select(appStore => appStore.users.userStationGeoIPs).pipe(filter((data) => data !== null));
                }
            }),
            mergeMap(a => a),
        );
    }

    public getPreferences(): Observable<IPreferencesBusiness> {
        return this.store.select(appStore => appStore.users.preferences).pipe(filter((data) => data !== null));
    }

    public getTwoFactorState(): Observable<boolean> {
        return this.store.select(appStore => appStore.users.twoFactorState).pipe(filter((data) => data !== null));
    }

    public getSliderSelection(): Observable<ISelectedTouch> {
        return this.store.select(appStore => appStore.config.touchConfig).pipe(filter((data) => data !== null));
    }

    public getEditorMode(): Observable<'scene-builder' | 'touch-editor'> {
        return this.store.select(appStore => appStore.config.editorMode);
    }

    public getFasterqLines(): Observable<List<FasterqLineModel>> {
        return this.store.select(appStore => appStore.fasterq.lines).pipe(filter((data) => data !== null));
    }

    public getFasterqTerminal(): Observable<FasterqLineModel> {
        return this.store.select(appStore => appStore.fasterq.terminal).pipe(filter((data) => data !== null));
    }

    public listenLogger(): Observable<ILog[]> {
        return this.store.select(store => store.config.logger);
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // LOGGING & DEBUGGING
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Logs a message to the application log store
     *
     * @param i_data - Log entry with severity and message
     *
     * @description
     * Adds timestamped log entries to the store-based logger.
     * Useful for debugging and tracking application events.
     *
     * @remarks
     * - Automatically adds timestamp in format "MM-DD-YY|hh:mm:ss A"
     * - Dispatches actionLogger to store the log entry
     * - Logs persist in store and can be viewed via listenLogger()
     *
     * @example
     * ```typescript
     * this.storeSelector.logger({
     *     severity: 'error',
     *     message: 'Failed to load scene data',
     *     date: '' // Added automatically
     * });
     * ```
     */
    public logger(i_data: ILog): void {
        const date = moment().format("MM-DD-YY|hh:mm:ss A");
        // if (Lib.DevShow(`logger ${i_data.severity} ${i_data.message}`);
        this.dispatch(actionLogger({ log: { severity: i_data.severity, message: i_data.message, date } }));
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // DISPATCH METHODS - Store Actions
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Dispatches multiple actions with staggered timing
     *
     * @param dispatches - Array of actions to dispatch sequentially
     * @param startTime - Delay before first action in ms (default: random 1-1500ms)
     * @param timelapse - Delay between subsequent actions in ms
     *
     * @description
     * Dispatches actions one at a time with delays to prevent overwhelming the system.
     * Useful for bulk operations that need controlled timing (e.g., loading multiple scenes).
     *
     * @remarks
     * - Uses RxJS timer() to schedule dispatches
     * - Mutates dispatches array (shifts elements as they're dispatched)
     * - Random start time prevents simultaneous execution from multiple components
     *
     * @example
     * ```typescript
     * // Load data with 2-second delays between each action
     * this.storeSelector.dispatchStaggered([
     *     actionLoadScenes(),
     *     actionLoadResources(),
     *     actionLoadStations()
     * ], 500, 2000);
     * ```
     */
    public dispatchStaggered(dispatches: Array<Action>, startTime, timelapse) {
        if (!startTime) startTime = _.random(1, 1500);
        const intervals$ = timer(startTime, timelapse)
            .pipe(
                take(dispatches.length))
            .subscribe((count) => {
                const fire = dispatches.shift();
                this.dispatch(fire);
            });
    }

    /**
     * Dispatches an action to the NgRx store
     *
     * @param action - NgRx action to dispatch
     *
     * @description
     * THE PRIMARY method for triggering store actions. All component-initiated state changes
     * should use this method instead of directly accessing the store.
     *
     * @remarks
     * - Centralized dispatch point for debugging and logging
     * - Uncomment Lib.DevShow line for dispatch debugging
     *
     * @example
     * ```typescript
     * // Update scene
     * this.storeSelector.dispatch(actionUpdateScene({ scene: updatedScene }));
     *
     * // Delete resource
     * this.storeSelector.dispatch(actionDeleteResource({ resourceId: '12345' }));
     *
     * // Select scene item
     * this.storeSelector.dispatch(actionSelectSceneItem({ sceneItemId: '67890' }));
     * ```
     */
    public dispatch(action: Action) {
        // Lib.DevShow('dispatching ' + action.type);
        this.store.dispatch(this.stampDrillPath(action));
    }

    /**
     * Stamp WHERE THE USER IS DRILLED TO onto an undoable scene-edit action, so a later UNDO/REDO can re-enter
     * that exact spot (group / component / playlist page / nested scene) and the reverted change is visible.
     * Every other action passes through untouched.
     *
     * This lives in dispatch() because it is the one chokepoint every tracked action goes through, and because
     * the drill position is only knowable HERE - at dispatch time, from live UI state. The reducer cannot see it:
     * ContainerService is not in the store at all, and the breadcrumbs are in `config` while the history lives in
     * `users`. Recording beats re-deriving - a group edit carries its PARENT scene's sceneId, so after the fact
     * there is nothing in the payload that says which group (or playlist page) it happened in.
     *
     * Costs one synchronous store read per tracked action; wieder keeps the stamp via trackActionPayload.
     */
    private stampDrillPath(action: Action): Action {
        if (!action || !UNDOABLE_ACTION_TYPES.includes(action.type)) return action;

        let crumbs: Array<any> = [];
        let sceneId: any = null;
        // plain store selects resolve synchronously, so take(1) gives us the current value inline
        this.store.select(store => store.config).pipe(take(1)).subscribe((config: any) => {
            crumbs = config?.uiSceneConfig?.breadcrumbs || [];
            sceneId = config?.uiSceneConfig?.selectedSceneId ?? null;
        });

        const drill: IDrillPath = {
            // copy: the stored crumbs array is live store state that later navigation replaces, and the stamp
            // must be a frozen snapshot of THIS moment
            crumbs: crumbs.map(c => ({...c})),
            editTarget: this.container?.componentEditTarget?.value ? {...this.container.componentEditTarget.value} : null,
            editorMode: this.container?.editorMode?.value ?? null,
            sceneId,
        };
        // cast: the stamp is history metadata riding alongside the payload, not part of the Action contract
        return {...action, [DRILL_PATH_KEY]: drill} as Action;
    }

    /**
     * Gets direct access to the NgRx store
     *
     * @returns Store<IAppState> - The NgRx store instance
     *
     * @remarks
     * Provides direct store access for advanced scenarios.
     * **IMPORTANT**: Components should prefer using selector methods instead of direct store access.
     */
    public get appStore() {
        return this.store;
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // UNDO/REDO SELECTORS - History Management
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Checks if undo operation is available
     *
     * @returns Observable<boolean> - True if undo is disabled (no history), false if undo is available
     *
     * @description
     * Uses ngrx-wieder for undo/redo state management. Returns true when undo button should be disabled.
     *
     * @remarks
     * - Inverted logic: true = cannot undo, false = can undo
     * - Use for disabling/enabling undo UI buttons
     * - Tracks scene edit history
     *
     * @example
     * ```typescript
     * this.storeSelector.getUndoable()
     *     .subscribe(undoDisabled => {
     *         this.undoButtonDisabled = undoDisabled;
     *     });
     * ```
     */
    public getUndoable(): Observable<boolean> {
        // return this.store.select((state) => !state.users.canUndo);
        return this.store.select(selectCanUndo()).pipe(map((canUndo) => !canUndo));
    }

    /**
     * Checks if redo operation is available
     *
     * @returns Observable<boolean> - True if redo is disabled (no future history), false if redo is available
     *
     * @description
     * Uses ngrx-wieder for undo/redo state management. Returns true when redo button should be disabled.
     *
     * @remarks
     * - Inverted logic: true = cannot redo, false = can redo
     * - Use for disabling/enabling redo UI buttons
     * - Tracks undone operations that can be redone
     *
     * @example
     * ```typescript
     * this.storeSelector.getRedoable()
     *     .subscribe(redoDisabled => {
     *         this.redoButtonDisabled = redoDisabled;
     *     });
     * ```
     */
    public getRedoable(): Observable<boolean> {
        // return this.store.select((state) => !state.users.canRedo);
        return this.store.select(selectCanRedo()).pipe(map((canRedo) => !canRedo));
    }

    /**
     * Gets paint/background image sources
     *
     * @returns Observable<IPaintSource[]> - Array of background paint sources
     *
     * @remarks
     * Background collections for scene styling
     */
    public getPaints(): Observable<IPaintSource[]> {
        return this.store.select((state) => state.users.painters).pipe(filter((data) => data !== null));
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // FASTERQ SELECTORS - Queue Management
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Listens to all FasterQ lines (queue lines)
     *
     * @returns Observable<List<FasterqLineModel>> - Immutable List of queue lines
     *
     * @description
     * Returns all FasterQ queue lines for the account. Lines are the top-level queue containers.
     *
     * @remarks
     * FasterQ is a queue management system for service businesses (retail, restaurants, etc.)
     */
    public listenFasterqLines(): Observable<List<FasterqLineModel>> {
        return this.store.select(store => store.fasterq.lines);
    }

    /**
     * Listens to FasterQ queues for the selected line
     *
     * @returns Observable<List<FasterqQueueModel>> - Queues belonging to selected line
     *
     * @description
     * Combines selected line ID with all queues to return only queues for the active line.
     * Filters by lineId match.
     *
     * @remarks
     * - Uses combineLatest to react to line selection changes
     * - Returns filtered list of queues for current line
     */
    public listenFasterqQueues(): Observable<List<FasterqQueueModel>> {
        const selected$ = this.store.select(store => store.config.fasterq.fasterqLineSelected);
        const queues$ = this.store.select(store => store.fasterq.queues);
        return combineLatest([selected$, queues$]).pipe(
            map(([lineId, queues]) => {
                return queues.filter((queue: FasterqQueueModel) => {
                    return queue.lineId == lineId;
                }) as List<FasterqQueueModel>;
            }),
            filter(value => value != null),
        );
    }

    /**
     * Listens to FasterQ analytics data
     *
     * @returns Observable<List<FasterqAnalyticsModel>> - Queue analytics and statistics
     *
     * @remarks
     * Analytics for queue performance, wait times, service metrics
     */
    public listenFasterqAnalytics(): Observable<List<FasterqAnalyticsModel>> {
        return this.store.select(store => store.fasterq.analytics);
    }

    /**
     * Listens to the currently selected FasterQ line
     *
     * @returns Observable<FasterqLineModel> - Selected line object
     *
     * @description
     * Combines selected line ID (from UI config) with lines data to return full line object.
     *
     * @remarks
     * Filters null - only emits when a line is selected and data exists
     */
    public listenFasterqLineSelected(): Observable<FasterqLineModel> {
        const selected$ = this.store.select(store => store.config.fasterq.fasterqLineSelected);
        const lines$ = this.store.select(store => store.fasterq.lines);
        return combineLatest([selected$, lines$]).pipe(
            map(([lineId, lines]) => {
                return lines.find((line: FasterqLineModel) => {
                    return line.lineId == lineId;
                });
            }),
            filter(value => value != null),
        );
    }

    /**
     * Listens to the currently selected FasterQ queue (service)
     *
     * @returns Observable<FasterqQueueModel> - Selected queue object
     *
     * @description
     * Combines selected queue/service ID with queues data to return full queue object.
     *
     * @remarks
     * Queues represent individual services within a line (e.g., "Cashier", "Returns")
     */
    public listenFasterqQueueModelSelected(): Observable<FasterqQueueModel> {
        const selected$ = this.store.select(store => store.config.fasterq.fasterqQueueSelected);
        const queues$ = this.store.select(store => store.fasterq.queues);
        return combineLatest([selected$, queues$]).pipe(
            map(([serviceId, queues]) => {
                return queues.find((queue: FasterqQueueModel) => {
                    return queue.serviceId == serviceId;
                });
            }),
            filter(value => value != null),
        );
    }

    /**
     * Listens to the selected FasterQ queue ID (UI state)
     *
     * @returns Observable<any> - Selected queue/service ID
     */
    public listenFasterqQueueSelected(): Observable<any> {
        return this.store.select(store => store.config.fasterq.fasterqQueueSelected);
    }

    /**
     * Listens to the "now servicing" customer number being polled
     *
     * @returns Observable<any> - Currently servicing customer data
     *
     * @remarks
     * Tracks which customer is currently being served at the queue
     */
    public listenFasterqQueueLastServicedPolled(): Observable<any> {
        return this.store.select(store => store.config.fasterq.fasterqNowServicing);
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // SVG & SCENE UTILITIES
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Gets SVG color configuration for selected item
     *
     * @returns Observable<ISVGConfig> - SVG color settings
     *
     * @remarks
     * Used for SVG colorization in the scene editor
     */
    public getSvgColors(): Observable<ISVGConfig> {
        return this.store.select(store => store.config.uiSceneConfig.sceneItemSvgColor);
    }

    /**
     * Listens to scene CRUD (Create/Update/Delete) operations
     *
     * @returns Observable<ICRUDScene> - Scene CRUD operation metadata
     *
     * @description
     * Tracks ongoing scene create/update/delete operations with status and results.
     *
     * @remarks
     * Used for handling scene save/update confirmation and error handling
     */
    public listenCRUDScene(): Observable<ICRUDScene> {
        return this.store.select(store => store.users.crudScene);
    }

    /**
     * Handles mime type anomalies for special JSON-based blocks
     *
     * @param i_sceneItem - Scene block to analyze
     * @returns MimeAnomalyType - Detected anomaly type ('STOCKS', 'XML', 'CATALOG', 'CUSTOM_RSS', 'JSON')
     *
     * @description
     * Some blocks (Stocks, XML, Catalog, Custom RSS) have special mime type handling.
     * This method detects and categorizes these anomalies for proper processing.
     *
     * @remarks
     * - Checks block player ID against known anomaly types
     * - Falls back to checking JSON aliases for special cases
     * - Returns 'JSON' as default for unrecognized types
     * - Used internally by scene processing logic
     */
    public handleMimeAnomalies(i_sceneItem: ISceneBlock): MimeAnomalyType {
        switch (i_sceneItem.$.player) {
            case this.blockLabels.STOCKS.id: {
                return "STOCKS";
            }
            case this.blockLabels.XML.id: {
                return "XML";
            }
            case this.blockLabels.CATALOG.id: {
                return "CATALOG";
            }
            case this.blockLabels.CUSTOMRSS.id: {
                return "CUSTOM_RSS";
            }
            default: {
                const playerId = this.isBlockIdJsonAliased(i_sceneItem);
                switch (playerId) {
                    // case this.blockLabels.NETFEED_NEWS.id: {
                    //     return 'NETFEED_NEWS';
                    // }
                    // case this.blockLabels.NETFEED_HOROSCOPE.id: {
                    //     return 'NETFEED_HOROSCOPE';
                    // }
                    default: {
                        return "JSON";
                    }
                }
            }
        }
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // CREATESELECTOR EXAMPLES - Performance Optimization Patterns
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * EXAMPLE: Basic createSelector usage for user data
     *
     * @returns Observable<IUser> - Current user via memoized selector
     *
     * @description
     * Demonstrates basic createSelector for selecting nested state.
     * createSelector provides automatic memoization - only recomputes when input state changes.
     *
     * @remarks
     * This is a simple example. See getUserCombinesExample3() for production-ready pattern.
     */
    public getUserMemoize(): Observable<IUser> {
        return this.store.select(
            createSelector((state: IAppState) => state.users,
                (userState) => {
                    return userState.selectedUser;
                },
            ),
        );
    }

    /**
     * EXAMPLE: Combining multiple createSelectors
     *
     * @returns Observable<string> - User's business ID
     *
     * @description
     * Demonstrates combining multiple selectors with createSelector.
     * This example combines two user selectors to extract businessId.
     *
     * @remarks
     * This is a demonstration. In practice, avoid recreating selectors on every call.
     * See getUserCombinesExample5() for the proper cached pattern.
     */
    public getUserCombinesExample(): Observable<string> {
        const users1$ = createSelector((state: IAppState) => state.users,
            (userState) => {
                return userState.selectedUser;
            },
        );
        const users2$ = createSelector((state: IAppState) => state.users,
            (userState) => {
                return userState.selectedUser;
            },
        );
        const selectFilteredItems = createSelector(users1$, users2$,
            (i_users1, i_users2) => {
                return i_users1.businessId;
            },
        );
        const finalObs$ = this.store.select(selectFilteredItems);
        return finalObs$;
    }

    /**
     * EXAMPLE: Optimized createSelector with caching (RECOMMENDED PATTERN)
     *
     * @returns Observable<IUser> - Current user via cached memoized selector
     *
     * @description
     * **THIS IS THE RECOMMENDED PATTERN** for production use.
     * Creates selector ONCE and caches it as class property for reuse.
     * Provides maximum performance through proper memoization.
     *
     * @remarks
     * - Checks if selector already exists before creating
     * - Stores selector in this.selectorGetUsers$ for reuse
     * - Only creates selector on first call
     * - Subsequent calls use cached selector for optimal performance
     * - This pattern prevents recreating selectors on every method call
     *
     * @example
     * ```typescript
     * // First call creates and caches selector
     * this.storeSelector.getUserCombinesExample3().subscribe(user => {...});
     *
     * // Subsequent calls reuse cached selector (faster)
     * this.storeSelector.getUserCombinesExample3().subscribe(user => {...});
     * ```
     */
    getUserCombinesExample3(): Observable<IUser> {
        if (!this.selectorGetUsers$) {
            this.selectorGetUsers$ = createSelector((state: IAppState) => state.users.selectedUser, (userState) => userState);
        }
        return this.store.select(this.selectorGetUsers$)
            .pipe(map((v: IUser) => {
                    return v;
                }),
            );
    }

    /**
     * EXAMPLE: Cached createSelector for IUserState
     *
     * @returns Observable<IUserState> - Complete user state slice
     *
     * @description
     * Similar to getUserCombinesExample3() but returns entire IUserState instead of just IUser.
     * Uses same caching pattern for optimal performance.
     */
    getUserCombinesExample4(): Observable<IUserState> {
        if (!this.selectorGetUsers$) {
            this.selectorGetUsers$ = createSelector((state: IAppState) => state.users, (userState) => userState);
        }
        return this.store.select(this.selectorGetUsers$)
            .pipe(map((v: IUserState) => {
                    return v;
                }),
            );
    }

    /**
     * EXAMPLE: Combining multiple state slices with cached createSelector
     *
     * @returns Observable<{u: IUser, c: IUIConfigState}> - Combined user and config state
     *
     * @description
     * **ADVANCED PATTERN** for combining multiple store slices efficiently.
     * Creates a combined selector ONCE, caches it, and reuses it.
     * Perfect for components that need data from multiple state slices.
     *
     * @remarks
     * - Combines users and config state into single object
     * - Creates selector only on first call
     * - Stores in this.selectorCombined$ for reuse
     * - Provides optimal performance for multi-slice selections
     * - Both slices benefit from memoization
     *
     * @example
     * ```typescript
     * this.storeSelector.getUserCombinesExample5()
     *     .subscribe(({ u, c }) => {
     *         console.log('User:', u);
     *         console.log('Config:', c);
     *     });
     * ```
     */
    getUserCombinesExample5(): Observable<{ u: IUser, c: IUIConfigState }> {
        if (!this.selectorCombined$) {
            // alert('create instance once selectorCombined$');
            const users = createSelector((state: IAppState) => state.users, (userState) => userState);
            const config = createSelector((state: IAppState) => state.config, (userState) => userState);
            this.selectorCombined$ = createSelector(users, config,
                (i_users1, i_config) => {
                    return { u: i_users1, c: i_config };
                },
            );
        }
        return this.store.select(this.selectorCombined$)
            .pipe(map((v: { u: IUser, c: IUIConfigState }) => {
                    return v;
                }),
            );
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // EVENT MANAGEMENT SELECTORS
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Gets all event blocks defined within scenes
     *
     * @returns Observable<Array<IEventBlock>> - All event blocks across all scenes
     *
     * @description
     * Scans all scenes and extracts event blocks (components that trigger/receive events).
     * Events enable inter-block communication and interactive functionality.
     *
     * @remarks
     * - Filters null (waits for scenes to load)
     * - Uses getAllEventsInScenes() utility to recursively find events in scene tree
     * - Events include: onClick, onLoad, onTimer, custom events, etc.
     */
    public getAllEventBlocksInScenes(): Observable<Array<IEventBlock>> {
        return this.store.select(appStore => appStore.users.scenes).pipe(
            filter(data => data != null),
            map(data => {
                return getAllEventsInScenes(data);
            }));
    }

    /**
     * Gets all events in the system (scenes + database event params)
     *
     * @returns Observable<{eventBlocks, eventParams}> - Combined events from scenes and database
     *
     * @description
     * Combines event blocks from scenes with event params from database to provide
     * a complete view of all events available in the system.
     *
     * @remarks
     * - Uses combineLatest to merge scene events and database event params
     * - Event params are stored event configurations in database
     * - Event blocks are blocks within scenes that use events
     *
     * @example
     * ```typescript
     * this.storeSelector.getAllEventsInSystem()
     *     .subscribe(({ eventBlocks, eventParams }) => {
     *         console.log('Scene events:', eventBlocks.length);
     *         console.log('Database events:', eventParams.length);
     *     });
     * ```
     */
    public getAllEventsInSystem(): Observable<{ eventBlocks: Array<IEventBlock>, eventParams: Array<IEventParam> }> {
        const eventParams$ = this.getEventParams();
        const eventsScenes$ = this.getAllEventBlocksInScenes();
        return combineLatest([eventsScenes$, eventParams$])
            .pipe(
                map(([eventBlocks, eventParams]) => {
                    return { eventBlocks, eventParams };
                }),
            );
    }

    /**
     * Gets all events in a structured format with unique event names
     *
     * @returns Observable<{eventBlocks, eventParams, eventStrings}> - Complete event system data
     *
     * @description
     * Provides the most comprehensive event view: combines scene events with database events,
     * and extracts unique event name strings for autocomplete/dropdown usage.
     *
     * @remarks
     * - Merges eventBlocks from scenes with eventParams from database
     * - Creates synthetic eventBlocks for database event params
     * - Extracts unique eventStrings (event names only) using Set
     * - EventType: "param list" for array values, "param object" for object values
     * - Perfect for event selection UI components
     *
     * @example
     * ```typescript
     * this.storeSelector.getAllEventsInSystemStructured()
     *     .subscribe(({ eventBlocks, eventParams, eventStrings }) => {
     *         // Use eventStrings for dropdown
     *         this.eventOptions = eventStrings;
     *
     *         // Use eventBlocks for detailed event info
     *         eventBlocks.forEach(block => {
     *             console.log(block.eventName, block.sceneName, block.blockName);
     *         });
     *     });
     * ```
     */
    public getAllEventsInSystemStructured(): Observable<{
        eventBlocks: Array<IEventBlock>,
        eventParams: Array<IEventParam>,
        eventStrings: Array<string>
    }> {
        return this.getAllEventsInSystem().pipe(map((values) => {
            const eventParams = values.eventParams;
            const eventBlocks = _.cloneDeep(values.eventBlocks);
            let eventStrings = [...new Set(eventBlocks.map((b) => b.eventName))];
            eventParams.forEach((i_eventParam) => {
                let eventType;
                if (i_eventParam?.event_type === 3) {
                    eventType = "param chart";
                } else if (i_eventParam?.event_type === 2 || isArray(i_eventParam?.event_value)) {
                    eventType = "param list";
                } else {
                    eventType = "param object";
                }
                eventBlocks.push({
                        eventName: i_eventParam.event_name,
                        eventType: eventType,
                        eventSceneId: "none",
                        eventBlockId: i_eventParam?.event_param_id,
                        blockName: i_eventParam.event_name,
                        sceneName: "event database",
                        eventBlockPlayer: eventType,
                    },
                );
            });
            eventStrings = [...new Set(eventBlocks.map((b) => b.eventName))];
            return { eventParams, eventBlocks, eventStrings };
        }));
    }

    /**
     * Gets all unique event name strings used in scenes
     *
     * @returns Observable<Array<string>> - Array of unique event names
     *
     * @description
     * Extracts just the event name strings from all scenes. Useful for event name
     * autocomplete, validation, and dropdown lists.
     *
     * @remarks
     * - Filters null (waits for scenes)
     * - Uses sceneFindEventInScenes() utility to extract event names
     * - Returns only unique strings (no duplicates)
     * - Lighter weight than getAllEventsInSystemStructured() when only names needed
     */
    public getAllEventStringsInScenes(): Observable<Array<string>> {
        return this.store.select(appStore => appStore.users.scenes).pipe(
            filter(data => data != null),
            map(data => {
                return sceneFindEventInScenes(data, true);
            }));
    }
}

/**
 * ==============================================================================
 * END OF STORE SELECTORS SERVICE
 * ==============================================================================
 *
 * This service provides ~200+ selector methods organized into these categories:
 *
 * 1. **Block Management** - Component labels, types, validation, templates
 * 2. **User & Authentication** - User data, privileges, access masks
 * 3. **Scene & Player** - Scene data, selections, player props, editor state
 * 4. **Resource Management** - Media library, storage stats, shared libraries
 * 5. **Station Management** - Digital signage players, connection status, geo IPs
 * 6. **Enterprise Features** - Business accounts, reseller info, white label
 * 7. **Adnet Features** - Ad network customers, rates, targets, packages
 * 8. **Shopping Cart** - Cart products, orders, billing, DynaWebs
 * 9. **Schedule Management** - Schedules, schedule events, calendar
 * 10. **FasterQ** - Queue lines, queue models, analytics, terminal
 * 11. **Event Management** - Event blocks, event params, event strings
 * 12. **Undo/Redo** - History management with ngrx-wieder
 * 13. **Dispatch Methods** - Action dispatching, staggered dispatching
 * 14. **Utility Methods** - Logging, window resize, SVG colors, CRUD
 *
 * **Key Patterns:**
 * - Observable selectors with filter() to wait for non-null values
 * - distinctUntilChanged() for performance optimization
 * - combineLatest() for combining multiple store slices
 * - createSelector() with caching for memoization
 * - Promise-based alternatives (getState, async methods)
 *
 * **Usage:**
 * Always inject StoreSelectorsService instead of directly accessing Store<IAppState>.
 * This ensures consistent data access patterns and centralized store logic.
 */



